<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import {
  dynamicTime,
  dateFormatLocalized,
  messageStamp,
} from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { contactAgendaName } from 'dashboard/helper/contactAgendaName';
import { inboxDisplayName } from 'dashboard/helper/inboxDisplayName';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useGuardedMedia } from 'dashboard/composables/useGuardedMedia';
import { useContactRemovalConfirmation } from 'dashboard/composables/useContactRemovalConfirmation';
import Avatar from 'next/avatar/Avatar.vue';
import ContactAPI from 'dashboard/api/contacts';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import EditContact from './EditContact.vue';
import AceleresClientDetails from './AceleresClientDetails.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import { isAceleresGroupContact } from 'dashboard/helper/aceleresGroupContact';
import ContactIdentities from 'dashboard/components-next/Contacts/ContactIdentities.vue';
import InstagramMergeSuggestions from 'dashboard/components-next/Contacts/InstagramMergeSuggestions.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import { isTeamNumberContact } from 'dashboard/helper/teamNumber';
import TeamNumberBadge from 'dashboard/components/widgets/TeamNumberBadge.vue';
import ContactPdfViewer from './ContactPdfViewer.vue';
import AceleresLinkPreviewsAPI from 'dashboard/api/aceleres/linkPreviews';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';
import {
  setContactMediaReturn,
  takeContactMediaReturn,
} from 'dashboard/composables/aceleresMediaViewerCoordinator';
import {
  createPdfFirstPagePreview,
  createPdfFirstPagePreviewFromUrl,
} from 'dashboard/helper/pdfPreviewHelper';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import { isGifAttachment } from 'dashboard/helper/gifMediaHelper';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import {
  instagramProfileUrl,
  normalizeInstagramUsername,
} from 'dashboard/helper/instagramProfile';

// Aceleres -- teto de download para desenhar a miniatura de um video/GIF no
// painel de contato. 12 MB cobre GIF do WhatsApp e video curto; acima disso a
// miniatura nao vale o trafego.
const MAX_PREVIEW_VIDEO_BYTES = 12 * 1024 * 1024;

// Aceleres -- teto para gerar a previa da primeira pagina de um PDF. Acima
// disso o balao fica so com o selo do tipo, sem imagem.
const MAX_PREVIEW_PDF_BYTES = 15 * 1024 * 1024;

// Aceleres -- quantas previas de PDF sao geradas ao mesmo tempo. Cada uma
// baixa o arquivo e desenha a pagina num canvas; uma lista com muitos PDFs
// disparando tudo de uma vez travava a aba. Quando uma termina o render
// reagenda a proxima sozinho.
const MAX_CONCURRENT_PDF_PREVIEWS = 2;

// Aceleres -- quantas previas de link sao buscadas ao mesmo tempo. Cada uma e
// uma leitura de pagina externa feita pelo servidor; uma conversa com muitos
// links disparando tudo de uma vez seguraria os workers do Puma.
const MAX_CONCURRENT_LINK_PREVIEWS = 3;

// Aceleres -- teto de paginas de anexo que o painel busca alem da primeira.
// Cinco paginas sao 500 midias; alem disso o custo da espera nao paga o que se
// ganha em lista.
const MAX_ATTACHMENT_HISTORY_PAGES = 5;

export default {
  components: {
    EditContact,
    AceleresClientDetails,
    Avatar,
    ContactMergeModal,
    ContactIdentities,
    InstagramMergeSuggestions,
    EmojiInput,
    TeamNumberBadge,
    ContactPdfViewer,
    ForwardMessageModal,
    FileIcon,
    Icon,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    whatsappGroupData: {
      type: Object,
      default: null,
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    channelType: {
      type: String,
      default: '',
    },
    inboxId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: [
    'panelClose',
    'groupSectionNavigate',
    'groupUpdated',
    // O painel pai renderiza os accordions antigos (Notas, atributos,
    // campanhas e Macros). Este evento permite que ele os mantenha dentro da
    // aba Ações sem desmontá-los nem remover qualquer funcionalidade.
    'contactTabChanged',
  ],
  setup() {
    const { isAdmin } = useAdmin();
    // Abrir uma midia da conversa fecha o painel de contato; a foto do perfil
    // conserva o painel aberto. O estado fica no useUISettings.
    const { updateUISettings } = useUISettings();
    // Aceleres 2026-08-18: a fila, o teto, o retry e o cache dos downloads
    // autenticados sairam daqui para `useGuardedMedia` — a tira de miniaturas
    // da galeria precisa exatamente do mesmo, e duas copias sairiam de
    // sincronia na primeira correcao feita so de um lado.
    const guardedMedia = useGuardedMedia();
    // Aceleres -- a cerimonia da remocao (o que sera preservado, o que sera
    // destruido, e o codigo quando ha conversa em aberto) e a MESMA da pagina
    // de Contatos. Vem de um composable justamente para as duas telas nao
    // divergirem.
    const contactRemoval = useContactRemovalConfirmation();

    return {
      isAdmin,
      updateUISettings,
      ...guardedMedia,
      contactRemoval,
    };
  },
  data() {
    return {
      // Aceleres 2026-08-19: a aba Documentos tem desenho proprio no telemovel
      // (sem a pre-visualizacao da primeira pagina, data no rodape a esquerda,
      // seleccao por toque longo). Parte disso e CSS, mas NAO renderizar a
      // imagem e decisao de template — por isso a largura vive aqui, e nao so
      // numa media query.
      isMobileViewport: false,
      showEditModal: false,
      showDeleteModal: false,
      isEditingName: false,
      editGroupDescription: '',
      isSavingGroupName: false,
      groupNameEditError: '',
      editName: '',
      showGroupEmojiPicker: false,
      showGroupPermissionModal: false,
      showMediaDetailsModal: false,
      activeContactTab: 'actions',
      activeGroupMobileSection: 'members',
      conversationHistory: [],
      isLoadingConversationHistory: false,
      // Listener de Esc da lista de midias — ver bindMediaModalEscapeKey.
      mediaModalEscapeHandler: null,
      activeMediaTab: 'media',
      allConversationAttachments: [],
      isLoadingAllConversationMedia: false,
      hasLoadedAllConversationMedia: false,
      // Aceleres -- o chat carrega por paginas conforme o atendente sobe, mas
      // o painel de contato mostra a conversa INTEIRA. Estes dois campos sao o
      // que falta para isso: as paginas de anexo alem da primeira (a primeira
      // ja vive na store, via fetchAllAttachments) e as mensagens que citam
      // algum endereco, lidas direto da API em vez das mensagens em memoria.
      historyAttachments: [],
      historyLinkMessages: [],
      loadedHistoryConversationId: null,
      isLoadingConversationHistoryMedia: false,
      // Aceleres -- elemento do painel que recebe o modal de midias via Teleport.
      mediaModalTarget: null,
      // Aceleres -- previa da primeira pagina do PDF, por URL de origem:
      // { preview: dataURL, pageCount }.
      pdfPreviews: {},
      // Aceleres -- titulo e descricao da pagina de cada link, por URL. Quem
      // le as meta tags e o backend; aqui fica so o resultado.
      linkPreviews: {},
      pdfPreviewsPending: {},
      pdfPreviewsFailed: {},
      // Aceleres -- selecao multipla na aba de documentos.
      selectedItemIds: [],
      // Aceleres 2026-08-19: busca das abas de Documentos e Links (so
      // telemovel). Vive aqui, e nao por aba, porque e limpa a cada troca de
      // aba e ao fechar a janela -- ver `setMediaDetailsTab`.
      mediaSearchQuery: '',
      // Aceleres 2026-08-18: toque longo na grelha de midia (so telemovel).
      // No telemovel nao existe `:hover`, e o quadradinho de selecao da grelha
      // esconde-se por opacidade justamente no hover -- ou seja, ate agora NAO
      // havia como selecionar midia num ecra de toque. Este e o gesto de
      // entrada; depois do primeiro item, `selectingInsteadOfOpening` ja fazia
      // o resto.
      mediaLongPressTimer: null,
      mediaLongPressPoint: null,
      mediaLongPressHandled: false,
      pinningSelectedItem: false,
      showForwardDocumentsModal: false,
      pdfViewerItem: null,
      pdfViewerReturnTab: null,
      pdfViewerMessage: null,
    };
  },
  computed: {
    // Aceleres -- selo "Número da Equipe" no painel de informações do contato.
    isTeamNumber() {
      return isTeamNumberContact(this.contact);
    },
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      storeSelectedChatAttachments: 'getSelectedChatAttachments',
      currentUser: 'getCurrentUser',
    }),
    selectedChatAttachments() {
      return this.storeSelectedChatAttachments;
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return {
        ...(this.currentChat?.meta?.sender?.additional_attributes || {}),
        ...(this.currentChat?.meta?.sender?.custom_attributes || {}),
        ...(this.contact.additional_attributes || {}),
        ...(this.contact.custom_attributes || {}),
      };
    },
    isMergeBlockedForGroup() {
      return this.isWhatsappGroup || isAceleresGroupContact(this.contact);
    },
    isWhatsappGroup() {
      return (
        this.additionalAttributes.is_whatsapp_group === true ||
        this.additionalAttributes.is_whatsapp_group === 'true'
      );
    },
    whatsappGroup() {
      const storedGroup = this.additionalAttributes.whatsapp_group || {};
      const currentGroup = this.whatsappGroupData;
      if (currentGroup?.jid && currentGroup.jid === storedGroup.jid) {
        return currentGroup;
      }
      return storedGroup;
    },
    groupParticipants() {
      const participants = this.whatsappGroup.participants;
      return Array.isArray(participants) ? participants : [];
    },
    groupParticipantCount() {
      const countCandidates = [
        this.whatsappGroup.participants_count,
        this.whatsappGroup.participant_count,
        this.whatsappGroup.members_count,
        this.whatsappGroup.member_count,
        this.additionalAttributes.whatsapp_group_participants_count,
        this.additionalAttributes.whatsapp_group_members_count,
        this.additionalAttributes.participants_count,
        this.additionalAttributes.members_count,
      ];
      const count = countCandidates.find(value => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) && numericValue >= 0;
      });

      if (count !== undefined) return Number(count);

      return this.groupParticipants.length;
    },
    groupMemberLabel() {
      const suffix = this.groupParticipantCount === 1 ? 'membro' : 'membros';
      return `${this.groupParticipantCount} ${suffix}`;
    },
    groupTypeLabel() {
      return 'Grupo';
    },
    groupMobileNavigationLabel() {
      return 'Navegação do grupo';
    },
    groupMobileSections() {
      return [
        { id: 'members', label: 'Membros', icon: 'i-lucide-users' },
        { id: 'media', label: 'Mídias', icon: 'i-lucide-images' },
        {
          id: 'settings',
          label: 'Configurações',
          icon: 'i-lucide-settings-2',
        },
      ];
    },
    groupMetaSeparator() {
      return '·';
    },
    groupNameLabel() {
      return 'Nome do grupo';
    },
    addGroupEmojiLabel() {
      return 'Adicionar emoji ao nome do grupo';
    },
    saveGroupNameLabel() {
      return 'Salvar nome do grupo';
    },
    editGroupNameLabel() {
      return 'Editar nome do grupo';
    },
    groupPermissionInfoLabel() {
      return 'Regras para edição do nome do grupo';
    },
    groupPermissionMessage() {
      return 'Somente admins podem editar os dados deste grupo';
    },
    groupPermissionButtonLabel() {
      return 'OK';
    },
    canEditGroupName() {
      const groupAdminFlag = [
        this.whatsappGroup.current_user_is_admin,
        this.whatsappGroup.currentUserIsAdmin,
        this.whatsappGroup.is_current_user_admin,
      ].find(value => value !== undefined);

      if (groupAdminFlag !== undefined) {
        return groupAdminFlag === true || groupAdminFlag === 'true';
      }

      const currentUserIdentifiers = [
        this.currentUser?.id,
        this.currentUser?.phone_number,
        this.currentUser?.phoneNumber,
        this.currentUser?.identifier,
        this.currentUser?.custom_attributes?.phone_number,
        this.currentUser?.additional_attributes?.phone_number,
      ]
        .map(identifier => this.normalizeGroupIdentifier(identifier))
        .filter(Boolean);

      const currentParticipant = this.groupParticipants.find(participant => {
        if (!participant || typeof participant === 'string') return false;
        if (participant.is_me || participant.isMe) return true;

        const participantIdentifiers = [
          participant.id,
          participant.user_id,
          participant.phone_number,
          participant.phoneNumber,
          participant.identifier,
          participant.jid,
        ]
          .map(identifier => this.normalizeGroupIdentifier(identifier))
          .filter(Boolean);

        return participantIdentifiers.some(identifier =>
          currentUserIdentifiers.includes(identifier)
        );
      });

      return Boolean(
        currentParticipant &&
          (currentParticipant.is_owner ||
            currentParticipant.is_admin ||
            currentParticipant.admin ||
            ['owner', 'creator', 'admin'].includes(currentParticipant.role))
      );
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    isWhatsappConversation() {
      return (
        this.channelType === 'Channel::Whatsapp' ||
        this.channelType === 'Channel::Api'
      );
    },
    isInstagramConversation() {
      return (
        this.channelType === 'Channel::Instagram' ||
        String(this.contact?.identifier || '').startsWith('instagram:')
      );
    },
    isMessengerConversation() {
      if (this.channelType === 'Channel::Instagram') return false;
      return (
        this.channelType === 'Channel::FacebookPage' ||
        String(this.contact?.identifier || '').startsWith('messenger:')
      );
    },
    instagramUsername() {
      return normalizeInstagramUsername(this.socialProfiles.instagram);
    },
    instagramProfileUrl() {
      return instagramProfileUrl(this.instagramUsername);
    },
    hasInstagramProfile() {
      return Boolean(this.instagramProfileUrl);
    },
    isTrainingContactContext() {
      const senderId = Number(this.currentChat?.meta?.sender?.id || 0);
      const contactId = Number(this.contact?.id || 0);

      return (
        contactId > 0 &&
        senderId === contactId &&
        isTrainingConversation(this.currentChat, this.currentUser)
      );
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    contactDisplayName() {
      // Exibição segue a agenda da conexão desta conversa. Os formulários de
      // edição continuam lendo `contact.name` de propósito: o que se edita ali
      // é o nome global do contato, que não pode ser sobrescrito pela leitura
      // particular de um vendedor. Ver helper/contactAgendaName.
      const displayName = contactAgendaName(this.contact, this.inboxId);
      return displayName || this.contact.phone_number || 'Contato';
    },
    contactDisplayPhone() {
      if (this.isInstagramConversation) {
        return this.instagramUsername ? `@${this.instagramUsername}` : '';
      }

      return (
        this.contact.phone_number ||
        this.contact.phoneNumber ||
        (String(this.contact.identifier || '').startsWith('instagram:')
          ? ''
          : this.contact.identifier) ||
        ''
      );
    },
    mobileContactPhone() {
      const phone = this.contactDisplayPhone;
      const digits = String(phone || '').replace(/\D/g, '');
      if (!/^55\d{10,11}$/.test(digits)) return phone;
      const local = digits.slice(4);
      return `+55 ${digits.slice(2, 4)} ${local.slice(0, -4)}-${local.slice(-4)}`;
    },
    mobileContactName() {
      return (
        contactAgendaName(this.contact, this.inboxId) ||
        this.mobileContactPhone ||
        this.contactDisplayName
      );
    },
    shouldShowContactDisplayPhone() {
      if (this.isWhatsappGroup || !this.contactDisplayPhone) return false;

      const normalize = value =>
        String(value || '')
          .replace(/^group:/, '')
          .replace(/\D/g, '');
      return (
        normalize(this.contactDisplayName) !==
        normalize(this.contactDisplayPhone)
      );
    },
    // Aceleres -- so da pra ampliar quando existe foto de verdade; sem ela o
    // Avatar mostra as iniciais e nao ha o que abrir.
    hasContactPhoto() {
      return Boolean(this.contact?.large_thumbnail || this.contact?.thumbnail);
    },
    // A foto da ficha usa a melhor variante disponível. A versão grande chega
    // a 700px quando a fonte tem resolução suficiente e preserva as dimensões
    // originais quando ampliar só destacaria compressão e borrado. As listas e
    // o cabeçalho continuam usando a miniatura leve.
    contactAvatarSrc() {
      return this.contact?.large_thumbnail || this.contact?.thumbnail || '';
    },
    mediaLinksDocsTitle() {
      return 'Mídia, links e docs';
    },
    viewAllMediaLabel() {
      return 'Ver todas as mídias';
    },
    allMediaTitle() {
      return 'Todas as mídias';
    },
    goToMessageTitle() {
      return 'Ir para a mensagem';
    },
    emailActionTitle() {
      return 'E-mail';
    },
    // Aceleres -- a store guarda so a primeira pagina de anexos (100 itens, os
    // mais recentes); historyAttachments traz o resto da conversa. Juntar por
    // id mantem o anexo que acabou de chegar ao vivo sem duplicar o que veio
    // pelas duas vias.
    conversationAttachments() {
      const byKey = new Map();
      // Aceleres 2026-08-17: a versão da STORE vence a do histórico, e por isso
      // vem depois (o `Map` deixa o último ganhar).
      //
      // As duas trazem o mesmo anexo com endereços diferentes: a store traz o
      // do Active Storage, que abre sozinho num `<img>`; o histórico traz o
      // endpoint guardado, que só responde por axios com o cabeçalho de
      // autenticação. A grelha do painel lida com os dois (baixa o guardado e
      // cria um object URL), mas a GALERIA não: ela põe o endereço direto no
      // `<img>`, o navegador pede sem cabeçalho e leva 401 — ficava tudo em
      // branco menos o item aberto. Medido no log: 5x 401 em
      // `attachment_downloads/32968`.
      //
      // Preferir a store não afraca a guarda: são exatamente os anexos que o
      // balão da conversa já desenha com esse mesmo endereço. O guardado
      // continua a valer para o que só o histórico conhece.
      [...this.historyAttachments, ...(this.selectedChatAttachments || [])]
        .filter(Boolean)
        .forEach(attachment => {
          const key = this.attachmentKey(attachment);
          if (key) byKey.set(key, attachment);
        });

      return [...byKey.values()];
    },
    // Aceleres -- áudios não fazem parte de "Mídia, links e docs": não possuem
    // thumbnail e ocupavam slots da galeria renderizando <img> quebrado.
    visibleChatAttachments() {
      return this.sortNewestFirst(
        this.conversationAttachments.filter(
          attachment => !this.isHiddenFromMediaLists(attachment)
        )
      );
    },
    chatMediaAttachments() {
      return this.visibleChatAttachments.filter(attachment =>
        this.isPreviewableMedia(attachment)
      );
    },
    chatDocumentAttachments() {
      return this.visibleChatAttachments.filter(
        attachment => !this.isPreviewableMedia(attachment)
      );
    },
    // Aceleres -- o link carrega a mensagem de origem (direcao, horario,
    // estado de entrega), que e o que permite desenha-lo como balao igual ao
    // WhatsApp e levar de volta pra conversa. O MESMO endereco enviado varias
    // vezes aparece varias vezes, cada envio com o seu horario: e o que o
    // WhatsApp faz, e sem isso o atendente perdia de vista quando cada um foi
    // mandado. A unica juncao e por mensagem: as duas origens da lista (o que
    // esta em tela e o historico) trazem as mesmas mensagens, e sem a chave
    // abaixo cada link no meio da tela sairia duplicado.
    chatLinks() {
      // As mensagens em memoria cobrem o que esta em tela (inclusive a que
      // acabou de chegar); historyLinkMessages cobre o resto da conversa.
      const messages = [
        ...(this.currentChat?.messages || []),
        ...this.historyLinkMessages,
      ];
      const found = new Map();

      messages.forEach(message => {
        const content = message.content || '';
        const matches = content.match(/https?:\/\/[^\s<>"']+/g) || [];
        matches.forEach(url => {
          found.set(`${message.id}-${url}`, {
            url,
            createdAt: Number(message.created_at) || 0,
            messageId: message.id,
            isOutgoing: this.isOutgoingMessageType(message.message_type),
            status: message.status || '',
            content,
            sender: message.sender || null,
          });
        });
      });

      return [...found.values()].sort((a, b) => b.createdAt - a.createdAt);
    },
    mediaLinksDocsCount() {
      if (
        this.currentChat?.id &&
        (this.isLoadingConversationHistoryMedia ||
          this.loadedHistoryConversationId !== this.currentChat.id)
      ) {
        return null;
      }

      return (
        this.mediaDetailItems.length +
        this.documentDetailItems.length +
        this.linkDetailItems.length
      );
    },
    mediaPreviewItems() {
      const previewLimit = this.isMobileViewport ? 12 : 4;

      return this.chatMediaAttachments
        .slice(0, previewLimit)
        .map((attachment, index) => ({
          id: `media-${attachment.id || index}`,
          type: 'media',
          kind: this.attachmentKind(attachment),
          isGif: isGifAttachment(attachment),
          title: this.attachmentFileName(attachment, 'Mídia'),
          url: attachment.data_url || attachment.dataUrl || '',
          thumbUrl: this.attachmentThumbUrl(attachment),
          attachment,
        }));
    },
    mediaDetailsAttachments() {
      const attachments = this.hasLoadedAllConversationMedia
        ? this.allConversationAttachments
        : this.conversationAttachments;

      return this.sortNewestFirst(
        attachments.filter(attachment => !this.isHiddenFromMediaLists(attachment))
      );
    },
    mediaDetailItems() {
      return this.mediaDetailsAttachments
        .filter(attachment => this.isPreviewableMedia(attachment))
        .map((attachment, index) => this.toMediaDetailItem(attachment, index));
    },
    documentDetailItems() {
      return this.mediaDetailsAttachments
        .filter(attachment => !this.isPreviewableMedia(attachment))
        .map((attachment, index) => this.toDocumentDetailItem(attachment, index));
    },
    // Aceleres 2026-08-19: a busca so existe onde ha nome para procurar --
    // documento tem nome de ficheiro e link tem endereco. A grelha de Midia
    // fica de fora: ali os itens sao miniaturas sem texto.
    isSearchableMediaTab() {
      return (
        this.activeMediaTab === 'documents' || this.activeMediaTab === 'links'
      );
    },
    mediaSearchTerm() {
      return this.normalizeForSearch(this.mediaSearchQuery);
    },
    mediaSearchPlaceholder() {
      return this.activeMediaTab === 'documents'
        ? 'Pesquisar documentos'
        : 'Pesquisar links';
    },
    clearMediaSearchLabel() {
      return 'Limpar pesquisa';
    },
    // A lista que a tela desenha. A selecao continua a olhar
    // `activeMediaTabItems` de proposito: filtrar o que ja estava selecionado
    // apagaria a selecao ao escrever na busca.
    visibleMediaTabItems() {
      if (!this.isSearchableMediaTab || !this.mediaSearchTerm) {
        return this.activeMediaTabItems;
      }

      return this.activeMediaTabItems.filter(item =>
        [item.title, item.url, item.host, item.caption].some(campo =>
          this.normalizeForSearch(campo).includes(this.mediaSearchTerm)
        )
      );
    },
    linkDetailItems() {
      return this.chatLinks.map((link, index) => ({
        id: `link-${link.messageId || index}-${index}`,
        type: 'link',
        // Sem titulo da pagina o cartao fica como o do WhatsApp para um link
        // sem previa: dominio em cima, o endereco no meio e o dominio embaixo.
        title: this.linkPreviews[link.url]?.title || this.linkHostname(link.url),
        description: this.linkPreviews[link.url]?.description || link.url,
        host: this.linkHostname(link.url),
        url: link.url,
        createdAt: link.createdAt,
        isOutgoing: link.isOutgoing,
        status: link.status,
        messageId: link.messageId,
        senderName:
          link.sender?.name ||
          (link.isOutgoing ? this.currentUser?.name : this.contactDisplayName),
        senderAvatar: link.sender?.thumbnail || link.sender?.avatar_url || '',
        displaySender: link.isOutgoing
          ? 'Você'
          : link.sender?.name || this.contactDisplayName,
        displayRecipient: link.isOutgoing ? this.contactDisplayName : 'Você',
        // O texto da mensagem serve de legenda quando ela nao e so o link,
        // como no WhatsApp.
        caption: this.linkCaption(link),
      }));
    },
    activeMediaTabItems() {
      if (this.activeMediaTab === 'documents') {
        return this.documentDetailItems;
      }

      if (this.activeMediaTab === 'links') {
        return this.linkDetailItems;
      }

      return this.mediaDetailItems;
    },
    // Aceleres -- so as midias da conversa aberta podem ir para o GalleryView.
    // Ele resolve a mensagem dentro de getSelectedChat e, quando nao acha, cai
    // no conversation_id da conversa atual -- responder/excluir/fixar acabariam
    // agindo na conversa errada para midias vindas de "todas as conversas".
    galleryAttachments() {
      return this.visibleChatAttachments.filter(attachment =>
        this.isPreviewableMedia(attachment)
      );
    },
    // Aceleres -- galeria e visualizador de PDF nascem fora do painel e ficam
    // abaixo dele no empilhamento; ambos precisam do mesmo rebaixamento.
    hasOverlayOpen() {
      // O leitor de PDF e o encaminhamento mobile sao teleportados para fora
      // do painel. Ambos precisam que o painel saia do z-index maximo enquanto
      // estiverem abertos para receberem os toques.
      return Boolean(
        this.pdfViewerItem ||
          (this.isMobileViewport && this.showForwardDocumentsModal)
      );
    },
    // Todas as listas juntas. Os ids ja nascem com prefixo por tipo
    // (`media-`, `document-`, `link-`), entao nao ha como um id de uma aba
    // casar com o de outra — a lista plana de seleccionados serve as tres.
    allDetailItems() {
      return [
        ...this.mediaDetailItems,
        ...this.documentDetailItems,
        ...this.linkDetailItems,
      ];
    },
    // Aceleres 2026-08-19: no telemovel a seleccao atravessa as abas — pode-se
    // marcar uma midia, um documento e um link e encaminhar os tres de uma vez,
    // e trocar de aba nao apaga nada. Por isso a lista de seleccionados sai de
    // TODAS as listas, e nao so da aba aberta: se saisse da aba, o que ficou
    // marcado noutra sumia da conta e da accao, mesmo continuando marcado.
    //
    // No computador continua a valer so a aba aberta: la a barra de accoes tem
    // botoes que dependem do tipo (baixar, destacar) e agir sobre o que nao
    // esta em tela seria uma surpresa.
    selectedItems() {
      const universo = this.isMobileViewport
        ? this.allDetailItems
        : this.activeMediaTabItems;

      return universo.filter(item => this.selectedItemIds.includes(item.id));
    },
    hasItemSelection() {
      return this.selectedItemIds.length > 0;
    },
    // Aceleres 2026-08-18: fixar age sobre UMA mensagem de cada vez (a acao
    // `pinMessage` alterna uma so). A conversa aceita ate quatro fixadas, mas
    // com dois itens selecionados o botao continua fora -- nao ha o que fixar
    // "em conjunto".
    canPinSelection() {
      return (
        this.selectedItemIds.length === 1 &&
        Boolean(this.selectedItems[0]?.messageId)
      );
    },
    // Aceleres -- encaminhar, destacar e apagar agem sobre a MENSAGEM, e nem
    // toda mensagem esta na store (item vindo da lista de "todas as
    // conversas", ou de um trecho ainda nao carregado). As acoes so precisam
    // do id da mensagem e da conversa dona dela, entao quando a mensagem nao
    // esta na store montamos uma equivalente com o que o anexo ja carrega --
    // em vez de deixar o item de fora. Vale para as tres abas: e o mesmo
    // conjunto de acoes em midia, documentos e links.
    selectedItemMessages() {
      return this.selectedItems
        .map(
          item =>
            this.findMessageForItem(item) || {
              id: item.messageId,
              conversation_id: this.documentConversationId(item),
              content: '',
              attachments: item.attachment ? [item.attachment] : [],
            }
        )
        .filter(message => message.id);
    },
    // O encaminhamento vai por conversa (a API resolve os ids dentro dela),
    // entao uma selecao que mistura conversas nao tem um destino unico.
    selectedItemsConversationId() {
      const ids = [
        ...new Set(
          this.selectedItemMessages.map(message =>
            Number(message.conversation_id || this.currentChat?.id)
          )
        ),
      ].filter(Boolean);

      return ids.length === 1 ? ids[0] : null;
    },
    mediaDetailsEmptyMessage() {
      // Aceleres 2026-08-19: com busca escrita a lista pode estar vazia sem a
      // conversa estar. Dizer "nenhum documento enviado nesta conversa" nesse
      // caso e mentira, e faz o utilizador pensar que a aba esta avariada.
      if (this.isSearchableMediaTab && this.mediaSearchTerm) {
        return `Nada encontrado para "${this.mediaSearchQuery.trim()}".`;
      }

      const labels = {
        media: 'Nenhuma mídia enviada nesta conversa.',
        documents: 'Nenhum documento enviado nesta conversa.',
        links: 'Nenhum link enviado nesta conversa.',
      };

      return labels[this.activeMediaTab];
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
    hasOverlayOpen(isOpen) {
      this.syncOverlayPanelStacking(isOpen);
    },
    // Aceleres 2026-08-17: o orçamento de miniaturas passa a ser POR CONVERSA.
    //
    // `originalPreviewDownloads` (o teto de arquivos originais, onde todo vídeo
    // cai porque não tem `thumb_url`) e `guardedMediaFailed` nasciam no `data()`
    // e não eram reiniciados em lado nenhum. Como este painel não é desmontado
    // ao trocar de conversa, os dois acumulavam para toda a vida dele: passadas
    // ~60 prévias de original, toda a mídia seguinte era marcada como falhada
    // e ficava no ícone de indisponível — "vai desaparecendo" à medida que se
    // percorrem conversas. E uma falha passageira condenava aquele item para
    // sempre, sem nunca voltar a tentar.
    //
    // Os object URLs já baixados NÃO são descartados de propósito: voltar a uma
    // conversa vista há pouco continua a desenhar na hora, sem baixar de novo.
    'currentChat.id': {
      handler() {
        this.resetGuardedMediaBudget();
        this.allConversationAttachments = [];
        this.hasLoadedAllConversationMedia = false;
        this.loadConversationHistoryMedia();
      },
      immediate: true,
    },
    // A tecla so e ouvida com a lista aberta: fechada, o Esc pertence ao
    // painel de dados do contato.
    showMediaDetailsModal: {
      handler(isOpen) {
        if (isOpen) this.bindMediaModalEscapeKey();
        else this.unbindMediaModalEscapeKey();
      },
      immediate: true,
    },
  },
  // Aceleres -- o modal precisa cobrir o painel inteiro (corpo, list-group e
  // footer-actions). Ancorar no viewport nunca fecha com a caixa do painel, que
  // e posicionada a partir da area da conversa. Entao ele e teleportado para o
  // proprio container do painel e preenchido com inset: 0. Sem o container
  // (layout inesperado) cai no body com o posicionamento antigo.
  mounted() {
    this.mediaModalTarget =
      this.$el?.closest?.('.contact-details-modal-container') || null;
    this.restoreMediaDetailsModal();
    this.bindMobileViewportQuery();
  },
  beforeUnmount() {
    this.unbindMobileViewportQuery();
    this.unbindMediaModalEscapeKey();
    this.syncOverlayPanelStacking(false);
    this.releaseGuardedMedia();
  },
  methods: {
    selectGroupMobileSection(section) {
      this.activeGroupMobileSection = section;
      this.$emit('groupSectionNavigate', section);
    },
    bindMobileViewportQuery() {
      if (!window.matchMedia) return;

      this.mobileViewportQuery = window.matchMedia('(max-width: 767px)');
      this.isMobileViewport = this.mobileViewportQuery.matches;
      this.onMobileViewportChange = event => {
        this.isMobileViewport = event.matches;
      };
      this.mobileViewportQuery.addEventListener(
        'change',
        this.onMobileViewportChange
      );
    },
    unbindMobileViewportQuery() {
      this.mobileViewportQuery?.removeEventListener?.(
        'change',
        this.onMobileViewportChange
      );
      this.mobileViewportQuery = null;
    },
    // Aceleres 2026-08-19 -- o rodape do documento no telemovel diz o DIA, como
    // no WhatsApp: "Hoje", "Ontem" ou a data curta (17/08/26). O rodape do
    // computador continua com `documentTimeLabel` (hora, dia da semana, data
    // completa) — sao duas leituras diferentes do mesmo carimbo, e cada ecra
    // mostra a sua.
    documentDayLabel(item) {
      const createdAt = Number(item?.createdAt) || 0;
      if (!createdAt) return '';

      const enviadoEm = new Date(createdAt * 1000);
      const hoje = new Date();
      const meiaNoite = data =>
        new Date(data.getFullYear(), data.getMonth(), data.getDate()).getTime();
      const diasDeDiferenca = Math.round(
        (meiaNoite(hoje) - meiaNoite(enviadoEm)) / 86400000
      );

      if (diasDeDiferenca <= 0) return 'Hoje';
      if (diasDeDiferenca === 1) return 'Ontem';

      return dateFormatLocalized(createdAt, 'dd/MM/yy');
    },
    dynamicTime,
    // Aceleres -- a direcao chega em duas escalas: as mensagens da store usam
    // o inteiro do enum (push_event_data) e os anexos/links vem com o nome.
    isOutgoingMessageType(messageType) {
      return ['outgoing', '1'].includes(String(messageType));
    },
    // Aceleres -- completa o que o chat nao carrega. O chat abre nas ultimas
    // mensagens e vai buscando as anteriores conforme o atendente sobe; ja
    // este painel mostra midia, documentos e links da conversa inteira, entao
    // busca direto na API: as paginas de anexo depois da primeira (a primeira
    // ja veio pela store) e as mensagens que citam algum endereco.
    async loadConversationHistoryMedia() {
      const conversationId = this.currentChat?.id;
      if (
        conversationId &&
        this.loadedHistoryConversationId === conversationId
      ) {
        return;
      }

      this.loadedHistoryConversationId = conversationId || null;
      this.historyAttachments = [];
      this.historyLinkMessages = [];
      this.isLoadingConversationHistoryMedia = Boolean(conversationId);
      if (!conversationId) return;

      try {
        const [attachments, links] = await Promise.all([
          this.fetchRemainingAttachmentPages(conversationId),
          ConversationAPI.getAllLinks(conversationId),
        ]);

        if (this.currentChat?.id !== conversationId) return;

        this.historyAttachments = attachments;
        this.historyLinkMessages = links.data?.payload || [];
      } catch (error) {
        if (this.currentChat?.id !== conversationId) return;
        // Sem o historico completo o painel continua com o trecho carregado.
        // Libera a marca para a proxima abertura tentar de novo: sob carga a
        // primeira ida pode simplesmente ter demorado demais.
        this.loadedHistoryConversationId = null;
      } finally {
        if (this.currentChat?.id === conversationId) {
          this.isLoadingConversationHistoryMedia = false;
        }
      }
    },
    // A primeira pagina (as 100 midias mais recentes) ja esta na store via
    // fetchAllAttachments, entao aqui se comeca na segunda -- pedi-la de novo
    // era uma ida lenta ao servidor para trazer o que ja estava em maos. Uma
    // pagina de cada vez e ate um teto: em paralelo, uma conversa com muita
    // midia disparava dez pedidos juntos e tirava o servidor do ar para todo o
    // resto.
    async fetchRemainingAttachmentPages(conversationId) {
      const collected = [];

      for (let page = 2; page <= MAX_ATTACHMENT_HISTORY_PAGES + 1; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await ConversationAPI.getAllAttachments(
          conversationId,
          page
        );
        const payload = data?.payload || [];
        if (!payload.length) break;

        collected.push(...payload);
        if (this.currentChat?.id !== conversationId) break;
      }

      return collected;
    },
    // Aceleres -- a midia aparece da mais recente para a mais antiga, tanto
    // nas quatro miniaturas dos dados do contato quanto na lista do modal. A
    // API devolve na ordem de criacao (mais antiga primeiro), entao pegar as
    // quatro primeiras trazia justamente as fotos mais velhas da conversa.
    // `created_at` vem em epoch no payload de anexos; sem ele o id serve de
    // desempate, ja que e crescente.
    sortNewestFirst(attachments) {
      // Duas escalas convivem na lista: o anexo vindo do endpoint traz
      // `created_at` em epoch, e o anexo que entrou pela mensagem ao vivo nao
      // traz nenhum (so o id). Comparar as duas na mesma conta jogava o anexo
      // recem-chegado -- justamente o mais novo -- pro fim da lista. Entao
      // `created_at` so decide quando os DOIS lados tem; senao vale o id, que
      // e crescente e serve de relogio.
      const stamp = attachment => Number(attachment?.created_at) || null;
      const identifier = attachment => Number(attachment?.id) || null;

      return [...(attachments || [])].sort((a, b) => {
        const stampA = stamp(a);
        const stampB = stamp(b);
        if (stampA && stampB && stampA !== stampB) return stampB - stampA;

        const idA = identifier(a);
        const idB = identifier(b);
        if (idA && idB) return idB - idA;

        // Um dos lados nasceu sem created_at (mensagem ao vivo): ele e o mais
        // recente e sobe.
        if (stampA && !stampB) return 1;
        if (!stampA && stampB) return -1;

        return 0;
      });
    },
    // Aceleres 2026-08-18: a resolucao das URLs guardadas mudou-se para o
    // composable `useGuardedMedia` (ver setup) — a tira de miniaturas da
    // galeria passou a precisar da mesma fila, do mesmo teto e do mesmo retry.
    // So imagem: video seria o arquivo inteiro em memoria so para pintar um
    // quadro, entao ele fica no icone de reserva (como ja ficava, ja que o
    // <video> tambem levava 401).
    //
    // Video e GIF (que chega do WhatsApp como MP4 mudo) entram na mesma
    // regra: sem previewer do Active Storage nao existe quadro pronto no
    // servidor, entao o proprio arquivo e baixado e o <video> pinta o quadro
    // (seekVideoPreviewFrame). Como um video pode ser grande, so ate
    // MAX_PREVIEW_VIDEO_BYTES; acima disso fica o icone, sem gastar a banda
    // do agente pra desenhar uma miniatura.
    guardedPreviewUrl(item) {
      if (item?.kind !== 'video') return item?.thumbUrl || '';

      const fileSize = Number(item?.attachment?.file_size) || 0;
      if (fileSize > MAX_PREVIEW_VIDEO_BYTES) return '';

      return item?.url || '';
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    selectContactTab(tab) {
      this.activeContactTab = tab;
      this.$emit('contactTabChanged', tab);
      if (tab === 'conversation-history') this.loadContactConversationHistory();
    },
    // O historico lista atendimentos, nao o cliente: cada card responde pela
    // conversa a que pertence. O backend manda o resumo pronto em
    // `aceleres_attendance` para que a situacao exista num lugar so.
    conversationAttendance(conversation) {
      return conversation?.aceleres_attendance || {};
    },
    conversationStatusLabel(conversation) {
      return (
        this.conversationAttendance(conversation).status_label ||
        conversation?.status ||
        'Sem status'
      );
    },
    conversationStatusKey(conversation) {
      return this.conversationAttendance(conversation).status || '';
    },
    conversationAssigneeName(conversation) {
      return this.conversationAttendance(conversation).assignee_name || '';
    },
    conversationResolvedAt(conversation) {
      const resolvedAt = this.conversationAttendance(conversation).resolved_at;
      if (!resolvedAt) return '';

      return dateFormatLocalized(resolvedAt, "dd/MM/yyyy 'às' HH:mm");
    },
    conversationInstanceName(conversation) {
      return inboxDisplayName(
        this.conversationAttendance(conversation).inbox_name
      );
    },
    openConversation(conversation) {
      if (!conversation?.id) return;

      this.$router.push(
        `/app/accounts/${this.$route.params.accountId}/conversations/${conversation.id}`
      );
    },
    async refreshAfterInstagramMerge() {
      const conversationId = this.currentChat?.id;
      if (!conversationId) return;

      await this.$store.dispatch('getConversation', {
        id: conversationId,
        forceRefresh: true,
      });
      this.$refs.contactIdentities?.refresh();
      this.conversationHistory = [];
      if (this.activeContactTab === 'conversation-history') {
        this.loadContactConversationHistory();
      }
    },
    async loadContactConversationHistory() {
      if (!this.contact?.id || this.isLoadingConversationHistory) return;

      this.isLoadingConversationHistory = true;
      try {
        const { data } = await ContactAPI.getConversations(this.contact.id);
        this.conversationHistory = data?.payload || [];
      } catch (error) {
        this.conversationHistory = [];
      } finally {
        this.isLoadingConversationHistory = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    openAceleresNewMessage() {
      const phone = this.contact.phone_number || this.contact.phoneNumber;
      if (!phone) {
        useAlert('Este contato nao tem telefone cadastrado.');
        return;
      }

      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      this.$router.push({
        name: 'home',
        params: { accountId: this.$route.params.accountId },
        query: { start_phone: phone },
      });
    },
    openEmail() {
      const email =
        this.contact.profileEmail ||
        this.contact.profile_email ||
        this.contact.email;
      if (!email) {
        useAlert('Este contato nao tem e-mail cadastrado.');
        return;
      }

      window.location.href = `mailto:${email}`;
    },
    openPhoneCall() {
      const phone = this.contact.phone_number || this.contact.phoneNumber;
      if (!phone) {
        useAlert('Este contato nao tem telefone cadastrado.');
        return;
      }

      window.location.href = `tel:${phone}`;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
      if (this.showDeleteModal) {
        this.contactRemoval.loadPreview(this.contact?.id);
      } else {
        this.contactRemoval.reset();
      }
    },
    confirmDeletion() {
      if (this.isTrainingContactContext) return;
      // Botao desabilitado nao basta: este modal confirma por clique num
      // <Button type="submit">, e um Enter no campo do codigo chegaria aqui.
      if (!this.contactRemoval.canConfirm.value) return;

      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.contactRemoval.reset();
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', {
          id,
          confirmationCode: this.contactRemoval.confirmationCode.value,
        });
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.$refs.mergeModal?.open();
    },
    startEditingName() {
      if (!this.canEditGroupName) return;
      this.editName = this.whatsappGroup.subject || this.contact.name || '';
      this.editGroupDescription = this.whatsappGroup.description || '';
      this.groupNameEditError = '';
      this.isEditingName = true;
      this.showGroupEmojiPicker = false;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    async saveNameEdit() {
      if (!this.isEditingName || this.isSavingGroupName) return;
      const trimmed = this.editName.trim();
      if (!this.isWhatsappGroup) {
        this.isEditingName = false;
        if (trimmed && trimmed !== this.contact.name) {
          this.updateContactField({ name: trimmed });
        }
        return;
      }
      this.groupNameEditError = '';
      if (!trimmed || trimmed.length > 100) {
        this.groupNameEditError = 'Informe um nome com até 100 caracteres.';
        return;
      }
      if (!this.editGroupDescription.trim()) {
        this.groupNameEditError = 'Preencha a descrição do grupo para salvar a alteração.';
        return;
      }
      const conversationId = this.currentChat?.id;
      if (!conversationId) {
        this.groupNameEditError = 'Abra a conversa do grupo para editar os dados.';
        return;
      }
      this.isSavingGroupName = true;
      this.showGroupEmojiPicker = false;
      try {
        const response = await window.axios.patch(
          `/api/v1/accounts/${this.$route.params.accountId}/aceleres/conversations/${conversationId}/group_details`,
          { name: trimmed, description: this.editGroupDescription }
        );
        this.$emit('groupUpdated', response.data.group);
        this.isEditingName = false;
        useAlert('Nome do grupo atualizado no WhatsApp.');
      } catch (error) {
        this.groupNameEditError =
          error.response?.data?.message ||
          'Não foi possível atualizar o nome do grupo.';
      } finally {
        this.isSavingGroupName = false;
      }
    },
    cancelNameEdit() {
      if (this.isSavingGroupName) return;
      this.showGroupEmojiPicker = false;
      this.isEditingName = false;
    },
    toggleGroupEmojiPicker() {
      this.showGroupEmojiPicker = !this.showGroupEmojiPicker;
    },
    appendGroupEmoji(emoji) {
      if (!emoji) return;
      this.editName = `${this.editName}${emoji}`;
      this.showGroupEmojiPicker = false;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },
    hideGroupEmojiPicker() {
      this.showGroupEmojiPicker = false;
    },
    normalizeGroupIdentifier(identifier) {
      return String(identifier || '')
        .replace(/^group:/, '')
        .replace(/@.*/, '')
        .replace(/\D/g, '');
    },
    openGroupPermissionModal() {
      this.showGroupPermissionModal = true;
    },
    closeGroupPermissionModal() {
      this.showGroupPermissionModal = false;
    },
    // Aceleres -- o mesmo bundle serve homolog e producao, entao a URL nao pode
    // ser fixa: com o dominio de homolog cravado aqui, o token do Chatwoot de
    // producao viajava para o Laravel de HOMOLOGACAO. O helper resolve o base
    // certo por hostname (em app.zoraxy.ai, o proxy same-origin do nginx).
    wameGroupApiUrl() {
      return `${resolveAceleresApiBase()}/api/wame/groups`;
    },
    wameGroupHeaders() {
      return {
        'X-Chatwoot-Token': this.currentUser?.access_token || '',
        'X-Chatwoot-Account-Id': this.$route.params.accountId || '',
      };
    },
    // Aceleres -- chave de um anexo para juntar as duas origens sem repetir.
    // O anexo que chega pela mensagem ao vivo tem `id`; o que vem do endpoint
    // NAO tem (ver attachments.json.jbuilder), so o endereco guardado — que
    // termina no id do anexo. Ler o id de la faz as duas origens caírem na
    // mesma chave. Exigir `id`, como se fazia, descartava tudo o que veio do
    // endpoint: o painel abria com midia e documentos vazios.
    attachmentKey(attachment) {
      const url = String(attachment?.data_url || attachment?.dataUrl || '');
      const fromUrl = url.match(/attachment_downloads\/(\d+)/);
      if (fromUrl) return `attachment-${fromUrl[1]}`;

      if (attachment?.id) return `attachment-${attachment.id}`;

      return url || null;
    },
    // Aceleres -- o que nunca entra em "Midia, links e docs": audio (nao tem
    // miniatura e ja vive no proprio balao) e figurinha (o WhatsApp tambem a
    // deixa fora da galeria; misturada com as fotos, virava ruido).
    isHiddenFromMediaLists(attachment) {
      return (
        this.isAudioAttachment(attachment) ||
        this.isStickerAttachment(attachment) ||
        this.isContactCardAttachment(attachment)
      );
    },
    // Aceleres 2026-08-18 -- o cartao de contato nao tem arquivo nenhum: nao e
    // midia nem documento. Sem esta excecao ele cairia na aba "Docs" como um
    // arquivo sem nome e sem endereco, porque essa lista e tudo o que sobra
    // depois da midia.
    isContactCardAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      return /^contact$/i.test(fileType);
    },
    isAudioAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return /^audio/i.test(fileType) || /^audio\//i.test(contentType);
    },
    isImageAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return /^image$/i.test(fileType) || /^image\//i.test(contentType);
    },
    // Aceleres -- figurinha e foto sao as duas file_type=image (mesmo webp);
    // sem checar wame_media_type nao da pra separar uma da outra no painel.
    // Aceleres -- vale a intencao gravada na mensagem, nao o arquivo que ficou
    // guardado: a figurinha vetorial chega como WebP desenhado no servidor e,
    // quando o desenho falha, como a animacao em JSON. As duas continuam sendo
    // figurinha e nenhuma delas pertence a lista de midias.
    isStickerAttachment(attachment) {
      const wameMediaType =
        attachment?.wame_media_type || attachment?.wameMediaType || '';

      return String(wameMediaType).toLowerCase() === 'sticker';
    },
    // Aceleres -- fonte unica do `kind` do item de midia (usado tanto na
    // previa de 4 quadrados quanto no modal), pra sticker/video nao precisarem
    // do mesmo ternario escrito duas vezes.
    attachmentKind(attachment) {
      if (this.isStickerAttachment(attachment)) return 'sticker';
      if (this.isVideoAttachment(attachment)) return 'video';
      return 'image';
    },
    isVideoAttachment(attachment) {
      const fileType = attachment?.file_type || attachment?.fileType || '';
      const contentType =
        attachment?.content_type || attachment?.contentType || '';
      return (
        /^(video|ig_reel)/i.test(fileType) || /^video\//i.test(contentType)
      );
    },
    isPreviewableMedia(attachment) {
      if (this.isAudioAttachment(attachment)) return false;

      const fileType = attachment?.file_type || attachment?.fileType || '';
      return /image|video|ig_reel/i.test(fileType);
    },
    // Aceleres -- sem thumbnail gerado, só imagem pode ir direto para o <img>.
    // Vídeo/áudio caíam em <img src="arquivo.mp4"> e quebravam o bloco.
    attachmentThumbUrl(attachment) {
      const thumbUrl = attachment?.thumb_url || attachment?.thumbUrl || '';
      if (thumbUrl) return this.withThumbVariant(thumbUrl);

      return this.isImageAttachment(attachment)
        ? this.withThumbVariant(
            attachment?.data_url || attachment?.dataUrl || ''
          )
        : '';
    },
    // Aceleres -- os previewers do ActiveStorage estão desligados
    // (config/application.rb), então vídeo nunca tem thumb_url. Assim como o
    // balão da conversa (Video.vue), deixamos o próprio <video> pintar o quadro.
    seekVideoPreviewFrame(event) {
      const video = event?.target;
      if (!video || video.currentTime > 0) return;

      // Primeiro quadro costuma ser preto; avança um pouco quando dá.
      if (Number.isFinite(video.duration) && video.duration > 0.6) {
        video.currentTime = 0.5;
      }
    },
    // Aceleres -- anexo externo (ig_reel/story) nao tem blob e continua sem
    // file_name mesmo depois do fix no jbuilder. A URL do ActiveStorage termina
    // no nome do arquivo, entao ela serve de reserva antes do rotulo generico.
    attachmentFileName(attachment, fallback) {
      const fileName = attachment?.file_name || attachment?.filename || '';
      if (fileName) return fileName;

      const url = attachment?.data_url || attachment?.dataUrl || '';
      const lastSegment = url.split('?')[0].split('/').pop() || '';

      try {
        const decoded = decodeURIComponent(lastSegment);
        if (/\.[a-z0-9]{2,5}$/i.test(decoded)) return decoded;
      } catch (error) {
        // URL com escape invalido -- mantem o rotulo generico.
      }

      return fallback;
    },
    toMediaDetailItem(attachment, index) {
      return {
        id: `media-${attachment.id || attachment.message_id || index}`,
        kind: this.attachmentKind(attachment),
        isGif: isGifAttachment(attachment),
        title: this.attachmentFileName(attachment, 'Mídia'),
        url: attachment.data_url || attachment.dataUrl || '',
        thumbUrl: this.attachmentThumbUrl(attachment),
        // Aceleres -- os mesmos dois campos que o item de documento carrega.
        // Encaminhar e destacar agem sobre a MENSAGEM: sem eles a midia so
        // funcionava quando a mensagem ja estava carregada na conversa aberta,
        // e a vinda de "todas as conversas" era descartada em silencio.
        messageId: attachment.message_id || attachment.messageId || null,
        conversationDisplayId: attachment.conversation_display_id || null,
        attachment,
      };
    },
    toDocumentDetailItem(attachment, index) {
      const messageType =
        attachment.message_type || attachment.messageType || '';

      return {
        id: `document-${attachment.id || attachment.message_id || index}`,
        type: 'document',
        title: this.attachmentFileName(attachment, 'Documento'),
        url: attachment.data_url || attachment.dataUrl || '',
        extension: attachment.extension || '',
        contentType: attachment.content_type || attachment.contentType || '',
        // Aceleres -- o balao precisa saber de que lado fica e se ja foi
        // entregue; ambos vem da mensagem (ver attachments.json.jbuilder).
        isOutgoing: this.isOutgoingMessageType(messageType),
        status: attachment.message_status || attachment.messageStatus || '',
        fileSize: Number(attachment.file_size || attachment.fileSize) || 0,
        createdAt: Number(attachment.created_at || attachment.createdAt) || 0,
        messageId: attachment.message_id || attachment.messageId || null,
        conversationDisplayId: attachment.conversation_display_id || null,
        attachment,
      };
    },
    // Aceleres -- rotulo do tipo: a extensao em maiusculas, com o
    // content_type como reserva pro anexo que chega sem extensao.
    documentTypeLabel(item) {
      const extension = String(item?.extension || '')
        .replace('.', '')
        .toUpperCase();
      if (extension) return extension;

      const contentType = String(item?.contentType || '');
      const subtype = contentType.split('/')[1] || '';
      return subtype ? subtype.toUpperCase().slice(0, 4) : 'ARQ';
    },
    documentSizeLabel(item) {
      const bytes = Number(item?.fileSize) || 0;
      if (!bytes) return '';

      const units = ['B', 'KB', 'MB', 'GB'];
      const exponent = Math.min(
        units.length - 1,
        Math.floor(Math.log(bytes) / Math.log(1024))
      );
      const value = bytes / 1024 ** exponent;
      const rounded =
        value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1);

      return `${rounded} ${units[exponent]}`;
    },
    documentMetaLabel(item) {
      const pages = this.documentPageCount(item);
      const pageLabel = pages
        ? `${pages} ${pages === 1 ? 'página' : 'páginas'}`
        : '';

      return [
        pageLabel,
        this.documentTypeLabel(item),
        this.documentSizeLabel(item),
      ]
        .filter(Boolean)
        .join(' • ');
    },
    // Aceleres -- mesma leitura do WhatsApp: hora quando e de hoje, dia da
    // semana na ultima semana, data completa depois disso.
    documentTimeLabel(item) {
      const createdAt = Number(item?.createdAt) || 0;
      if (!createdAt) return '';

      const date = new Date(createdAt * 1000);
      const days = (Date.now() - date.getTime()) / 86400000;

      if (days < 1) return messageStamp(createdAt, 'HH:mm');
      if (days < 7) return dateFormatLocalized(createdAt, 'EEEE');
      return dateFormatLocalized(createdAt, 'dd/MM/yyyy');
    },
    // Aceleres -- so o que saiu daqui tem confirmacao de entrega; a mensagem
    // recebida nunca mostra os tiques.
    documentStatusIcon(item) {
      if (!item?.isOutgoing) return '';

      const status = String(item?.status || '');
      if (status === 'read') return 'i-lucide-check-check';
      if (status === 'delivered') return 'i-lucide-check-check';
      if (status === 'failed') return 'i-lucide-circle-alert';
      return 'i-lucide-check';
    },
    // Aceleres -- mesma deteccao do balao do chat (File.vue): content_type
    // quando existe, senao a extensao, senao o final do nome do arquivo.
    isPdfItem(item) {
      const contentType = (item?.contentType || '').toLowerCase();
      if (contentType === 'application/pdf') return true;

      const extension = String(item?.extension || '')
        .replace('.', '')
        .toLowerCase();
      if (extension) return extension === 'pdf';

      return /\.pdf$/i.test(item?.title || '');
    },
    // Aceleres -- o visualizador de PDF precisa da mensagem real para os botoes
    // de acao (reagir, destacar, fixar). Ela so existe na store da conversa
    // aberta, entao PDF de outra conversa nao tem como abrir o modal.
    findMessageForItem(item) {
      const messageId = item?.attachment?.message_id || item?.messageId;
      if (!messageId) return null;

      return (
        (this.currentChat?.messages || []).find(
          message => String(message.id) === String(messageId)
        ) || null
      );
    },
    // Aceleres -- clicar no fundo do balao (documento ou link) leva pra
    // mensagem que o trouxe e pede o scroll.
    // Quando o documento veio de outra conversa (lista de "todas as
    // conversas"), navega pra ela antes.
    // O modal cobre so o painel de contato, com a conversa visivel ao lado:
    // na aba de links ele fica aberto, na mesma aba e na mesma rolagem, para
    // o atendente seguir de um link para o outro sem reabrir nada.
    // Aceleres 2026-08-17: com a selecao ATIVA, clicar num item marca-o em vez
    // de o abrir — em Midias, Documentos e Links. E o comportamento do
    // WhatsApp e de qualquer galeria: uma vez em modo de selecao, o alvo do
    // clique deixa de ser "ver" e passa a ser "escolher".
    //
    // O guard vive nos DOIS metodos que abrem coisas (este e goToItemMessage),
    // e nao nos varios `@click` do template: sao seis pontos de clique entre as
    // tres abas, e espalhar a condicao por todos era garantir que um ficasse
    // para tras. A caixa de selecao e o botao de baixar continuam com o seu
    // proprio `@click.stop`, entao nao passam por aqui.
    selectingInsteadOfOpening(item) {
      if (!this.hasItemSelection) return false;

      this.toggleItemSelection(item);
      return true;
    },
    async waitForMessageElement(messageId) {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const element = document.getElementById(`message${messageId}`);
        if (element) return true;

        // A hidratação atualiza a store antes de o Vue terminar de montar
        // todas as bolhas. Dar um frame ao render evita emitir o evento cedo
        // demais e perder o salto silenciosamente.
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => window.requestAnimationFrame(resolve));
      }

      return false;
    },
    async goToItemMessage(item) {
      if (this.selectingInsteadOfOpening(item)) return;

      const messageId = item?.messageId;
      if (!messageId) return;

      // O destino é a conversa, portanto o modal não pode permanecer sobre
      // ela nem esconder o alvo da navegação.
      this.closeMediaDetailsModal();
      this.updateUISettings({ is_contact_sidebar_open: false });

      const targetConversationId = item?.conversationDisplayId;
      if (
        targetConversationId &&
        Number(targetConversationId) !== Number(this.currentChat?.id)
      ) {
        await this.$router.push({
          path: `/app/accounts/${this.$route.params.accountId}/conversations/${targetConversationId}`,
          query: { messageId },
        });
      } else if (!document.getElementById(`message${messageId}`)) {
        // Aceleres 2026-08-17: o painel lista os anexos da conversa INTEIRA
        // (vem do servidor, paginado), mas o chat so tem em tela as ultimas
        // mensagens. Para um documento mais antigo do que essa janela o
        // elemento nao existe, e `onScrollToMessage` (MessagesView) nao faz
        // nada quando nao acha o alvo — o clique morria em silencio.
        // Medido no banco em 17/08: 59 dos 294 documentos estao a mais de 50
        // mensagens do fim, ou seja 1 em cada 5 nao levava a lugar nenhum.
        //
        // `hydrateConversationHistoryFully` ja existia na store para isto e
        // nao tinha nenhum consumidor; ela traz um lote do historico e junta
        // pedidos repetidos, entao clicar duas vezes nao busca duas vezes.
        await this.$store.dispatch(
          'hydrateConversationHistoryFully',
          this.currentChat?.id
        );
      }

      await this.waitForMessageElement(messageId);

      window.requestAnimationFrame(() => {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
      });
    },
    // Aceleres -- primeira pagina do PDF desenhada com pdf.js, reaproveitando
    // o helper que o balao do chat ja usa (pdfPreviewHelper). Os bytes vem do
    // blob que o download guardado ja trouxe, entao nao ha segunda descarga.
    documentPreviewSrc(item) {
      if (!this.isPdfItem(item) || !item?.url) return '';

      const cached = this.pdfPreviews[item.url];
      if (cached) return cached.preview;

      const fileSize = Number(item?.fileSize) || 0;
      if (fileSize > MAX_PREVIEW_PDF_BYTES) return '';

      const runningPreviews = Object.keys(this.pdfPreviewsPending).length;
      if (
        !this.pdfPreviewsPending[item.url] &&
        !this.pdfPreviewsFailed[item.url] &&
        runningPreviews < MAX_CONCURRENT_PDF_PREVIEWS
      ) {
        Promise.resolve().then(() => this.buildPdfPreview(item));
      }

      return '';
    },
    documentPageCount(item) {
      return this.pdfPreviews[item?.url]?.pageCount || 0;
    },
    async buildPdfPreview(item) {
      const url = item?.url;
      if (
        !url ||
        this.pdfPreviewsPending[url] ||
        this.pdfPreviews[url] ||
        this.pdfPreviewsFailed[url] ||
        Object.keys(this.pdfPreviewsPending).length >=
          MAX_CONCURRENT_PDF_PREVIEWS
      ) {
        return;
      }

      this.pdfPreviewsPending[url] = true;
      try {
        let preview;
        if (this.isGuardedMediaUrl(url)) {
          await this.resolveGuardedMedia(url);
          const blob = this.guardedMediaBlob(url);
          if (!blob) {
            this.pdfPreviewsFailed[url] = true;
            return;
          }
          preview = await createPdfFirstPagePreview(blob);
        } else {
          preview = await createPdfFirstPagePreviewFromUrl(url);
        }

        if (preview?.preview) {
          this.pdfPreviews[url] = preview;
        } else {
          this.pdfPreviewsFailed[url] = true;
        }
      } catch (error) {
        // Sem previa o balao segue com o selo do tipo. A marca de falha e o
        // que impede o render de pedir de novo em laco.
        this.pdfPreviewsFailed[url] = true;
      } finally {
        delete this.pdfPreviewsPending[url];
      }
    },
    // Aceleres -- mesma razao do download: a URL guardada nao abre sozinha
    // numa guia (401), entao a guia recebe o blob ja baixado.
    async openDocumentInNewTab(item) {
      const href = this.isGuardedMediaUrl(item?.url)
        ? await this.resolveGuardedMedia(item.url)
        : item?.url;
      if (!href) {
        useAlert('Não foi possível abrir este documento.');
        return;
      }

      window.open(href, '_blank', 'noopener,noreferrer');
    },
    // Aceleres -- so o dominio no titulo (e o que o WhatsApp destaca); o
    // endereco completo vai na linha de baixo.
    linkDomain(url) {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch (error) {
        return url;
      }
    },
    // O cartao mostra o endereco como ele e, com o www — e o que o WhatsApp
    // escreve na primeira e na ultima linha quando o link nao tem previa.
    linkHostname(url) {
      try {
        return new URL(url).hostname;
      } catch (error) {
        return url;
      }
    },
    // Aceleres -- busca titulo/descricao dos links da conversa, um punhado por
    // vez. Cada URL e pedida uma unica vez: a resposta (mesmo vazia) ja fica
    // guardada em linkPreviews e serve de marca de "ja tentei".
    async loadLinkPreviews() {
      const pending = this.chatLinks
        .map(link => link.url)
        .filter(url => !(url in this.linkPreviews));
      if (!pending.length) return;

      const unique = [...new Set(pending)];
      for (let index = 0; index < unique.length; index += MAX_CONCURRENT_LINK_PREVIEWS) {
        const batch = unique.slice(index, index + MAX_CONCURRENT_LINK_PREVIEWS);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(batch.map(url => this.fetchLinkPreview(url)));
      }
    },
    async fetchLinkPreview(url) {
      if (url in this.linkPreviews) return;

      this.linkPreviews = { ...this.linkPreviews, [url]: {} };
      try {
        const { data } = await AceleresLinkPreviewsAPI.show(url);
        this.linkPreviews = { ...this.linkPreviews, [url]: data || {} };
      } catch {
        // Sem previa o cartao continua com dominio e endereco, como antes.
      }
    },
    linkCaption(link) {
      const content = String(link?.content || '').trim();
      if (!content || content === link?.url) return '';

      return content.replace(link.url, '').trim();
    },
    documentConversationId(item) {
      return item?.conversationDisplayId || this.currentChat?.id || null;
    },
    cancelMediaLongPress() {
      if (this.mediaLongPressTimer) clearTimeout(this.mediaLongPressTimer);
      this.mediaLongPressTimer = null;
      this.mediaLongPressPoint = null;
    },
    onMediaTouchStart(item, event) {
      this.cancelMediaLongPress();
      this.mediaLongPressHandled = false;

      const touch = event.touches?.[0];
      if (!touch) return;

      this.mediaLongPressPoint = { x: touch.clientX, y: touch.clientY };
      this.mediaLongPressTimer = setTimeout(() => {
        this.mediaLongPressTimer = null;
        // `handled` trava o `click` que o navegador ainda vai disparar ao
        // soltar o dedo. Sem isto o toque longo selecionava e o click logo
        // atras cairia em `selectingInsteadOfOpening`, que DESELECIONAVA --
        // resultado liquido zero, como se o gesto nao fizesse nada.
        this.mediaLongPressHandled = true;
        this.toggleItemSelection(item);
        window.navigator?.vibrate?.(8);
      }, 450);
    },
    onMediaTouchMove(event) {
      if (!this.mediaLongPressTimer || !this.mediaLongPressPoint) return;

      const touch = event.touches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.mediaLongPressPoint.x;
      const deltaY = touch.clientY - this.mediaLongPressPoint.y;
      if (Math.hypot(deltaX, deltaY) > 12) this.cancelMediaLongPress();
    },
    onMediaTouchEnd() {
      this.cancelMediaLongPress();
    },
    onMediaCardClick(item) {
      if (this.mediaLongPressHandled) {
        this.mediaLongPressHandled = false;
        return;
      }

      this.openMediaPreviewItem(item);
    },
    // O menu nativo do navegador (guardar imagem, etc.) nao pode roubar o
    // toque longo. So e travado quando FOI o toque longo a dispara-lo: no
    // computador nao ha toque, logo nem `timer` nem `handled`, e o clique
    // direito continua a dar o menu do navegador como sempre deu.
    onMediaContextMenu(event) {
      if (this.mediaLongPressTimer || this.mediaLongPressHandled) {
        event.preventDefault();
      }
    },
    async pinSelectedItem() {
      if (!this.canPinSelection || this.pinningSelectedItem) return;

      const [item] = this.selectedItems;
      const conversationId = this.documentConversationId(item);
      if (!conversationId) return;

      this.pinningSelectedItem = true;
      try {
        await this.$store.dispatch('pinMessage', {
          accountId: this.$route.params.accountId,
          conversationId,
          messageId: item.messageId,
        });
        // A acao `pinMessage` engole os erros dela (`Promise.resolve(error)`),
        // entao nao da para saber pelo `catch` se correu bem. Quem diz o que
        // aconteceu e o estado que ficou na conversa: fixar e desfixar usam o
        // mesmo endpoint, e o servidor devolve a lista de fixadas.
        const isNowPinned = isMessagePinned(
          this.$store.getters.getConversationById(conversationId),
          item.messageId
        );
        useAlert(isNowPinned ? 'Mensagem fixada.' : 'Mensagem desafixada.');
        this.clearItemSelection();
      } finally {
        this.pinningSelectedItem = false;
      }
    },
    toggleItemSelection(item) {
      const id = item?.id;
      if (!id) return;

      this.selectedItemIds = this.selectedItemIds.includes(id)
        ? this.selectedItemIds.filter(selected => selected !== id)
        : [...this.selectedItemIds, id];
    },
    isItemSelected(item) {
      return this.selectedItemIds.includes(item?.id);
    },
    clearItemSelection() {
      this.selectedItemIds = [];
    },
    async downloadSelectedDocuments() {
      const selected = this.selectedItems;
      this.clearItemSelection();

      // Um de cada vez: o navegador ignora downloads simultaneos demais e o
      // arquivo vem do endpoint guardado, uma requisicao por item.
      await selected.reduce(
        (chain, item) => chain.then(() => this.downloadDocumentItem(item)),
        Promise.resolve()
      );
    },
    // Aceleres -- "destacar" e o mesmo star das mensagens (starMessage
    // alterna), o que mantem o destaque coerente com o balao no chat.
    async starSelectedItems() {
      const messages = this.selectedItemMessages;
      if (!messages.length) {
        useAlert('Nenhum item com mensagem para destacar.');
        return;
      }

      try {
        await Promise.all(
          messages.map(message =>
            this.$store.dispatch('starMessage', {
              accountId: this.$route.params.accountId,
              messageId: message.id,
            })
          )
        );
        this.clearItemSelection();
      } catch (error) {
        useAlert('Não foi possível destacar todos os itens.');
      }
    },
    openForwardDocumentsModal() {
      if (!this.selectedItemMessages.length) {
        useAlert('Nenhum item com mensagem para encaminhar.');
        return;
      }

      if (!this.selectedItemsConversationId) {
        useAlert('Selecione itens de uma mesma conversa para encaminhar.');
        return;
      }

      this.showForwardDocumentsModal = true;
    },
    onDocumentsForwarded() {
      this.showForwardDocumentsModal = false;
      this.clearItemSelection();
    },
    openPdfViewerItem(item, message) {
      // Aceleres 2026-08-17: abrir o PDF fecha a lista de midias e guarda a aba
      // de onde se saiu; fechar o leitor reabre a lista ali. O painel em si NAO
      // e desmontado (o leitor e teleportado para fora dele), entao a memoria
      // pode viver aqui — nao precisa do coordenador que a foto usa, onde a
      // arvore inteira morre.
      this.pdfViewerReturnTab = this.showMediaDetailsModal
        ? this.activeMediaTab
        : null;
      if (this.showMediaDetailsModal) this.closeMediaDetailsModal();

      this.pdfViewerItem = item;
      this.pdfViewerMessage = message;
    },
    closePdfViewer() {
      const returnTab = this.pdfViewerReturnTab;
      this.pdfViewerReturnTab = null;
      this.pdfViewerItem = null;
      this.pdfViewerMessage = null;

      if (returnTab) this.setMediaDetailsTab(returnTab);
    },
    // Aceleres -- documento nao-PDF baixa em vez de abrir aba, como no chat.
    // O atributo download so vale para mesma origem; se um dia a URL apontar
    // para outro host o navegador navega, que e exatamente o que ja acontecia.
    // Aceleres -- o endereco do anexo e o endpoint guardado, que exige os
    // cabecalhos de autenticacao; um <a href> nao os envia e o download saia
    // 401 sem aviso nenhum. Baixa pelo cliente autenticado e entrega o blob.
    async downloadDocumentItem(item) {
      const href = this.isGuardedMediaUrl(item?.url)
        ? await this.resolveGuardedMedia(item.url)
        : item?.url;
      if (!href) {
        useAlert('Não foi possível baixar este item.');
        return;
      }

      const link = document.createElement('a');
      link.href = href;
      link.download = item.title || '';
      link.rel = 'noreferrer noopener nofollow';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    // Aceleres -- a foto do contato abre no MESMO visualizador das imagens do
    // chat (GalleryView). Ela nao e um anexo de mensagem, entao vai como um
    // item montado na hora: sem message_id o GalleryView ja esconde as acoes
    // de mensagem (responder, reagir, fixar) e o `sender` alimenta o cabecalho
    // com o nome e o avatar do contato.
    openAvatarViewer(event) {
      if (!this.hasContactPhoto) return;

      const avatarRect = event.currentTarget.getBoundingClientRect();

      const avatarAttachment = {
        id: `contact-avatar-${this.contact?.id}`,
        file_type: 'image',
        // A versão grande respeita o teto de 700px sem ampliar fontes pobres;
        // `thumbnail` é a variante leve que serve a ficha e as listas.
        // O visualizador le `data_url` primeiro e so desce para `thumb_url` se
        // a primeira falhar ao carregar (ver `onActiveMediaError` no
        // GalleryView) — a miniatura e ultimo recurso, nunca a fonte padrao.
        data_url: this.contact.large_thumbnail || this.contact.thumbnail,
        thumb_url: this.contact.thumbnail,
        sender: {
          name: this.contactDisplayName,
          thumbnail: this.contact.thumbnail,
        },
        contactPhone: this.mobileContactPhone,
        contactName: this.mobileContactName,
        aceleresContactAvatar: true,
        aceleresMobileContactAvatar: this.isMobileViewport,
        avatarOrigin: {
          left: avatarRect.left,
          top: avatarRect.top,
          width: avatarRect.width,
          height: avatarRect.height,
        },
      };

      // A foto abre sozinha: se entrasse junto com as midias da conversa, o
      // usuario deslizaria do avatar pras fotos do chat como se fossem a mesma
      // colecao.
      this.openInConversationViewer(avatarAttachment, [avatarAttachment]);
    },
    openMediaDetailsModal() {
      this.setMediaDetailsTab('media');
    },
    // Sem acentos e sem caixa: quem procura "fatura" tem de achar "Fatura" e
    // "fatura de energia"; quem procura "sao" tem de achar "sao" e "sao".
    normalizeForSearch(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },
    closeMediaDetailsModal() {
      this.showMediaDetailsModal = false;
      this.mediaSearchQuery = '';
    },
    // Aceleres 2026-08-17: o Esc fecha a lista de midias, documentos e links.
    // O `@keydown.esc` que existia no <section> nunca disparava: o elemento
    // tem `tabindex="-1"` mas nada lhe dava foco, e sem foco a tecla nao
    // chegava ali. Como a lista e um `role="dialog"` visivel, ela ainda
    // suprimia o Esc do painel de dados do contato (ConversationSidebar) — o
    // resultado era a tecla nao fechar nem a lista nem o painel.
    //
    // Ouvir no documento, em captura, dispensa o foco; consumir o evento
    // impede que o mesmo Esc leve o painel de baixo junto.
    onMediaModalEscapeKey(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!this.showMediaDetailsModal) return;
      // Um visualizador aberto por cima (foto, video, PDF) fica com a tecla.
      if (
        document.querySelector(
          '.gallery-viewer-overlay, .acl-contact-pdf-viewer'
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.closeMediaDetailsModal();
    },
    bindMediaModalEscapeKey() {
      if (this.mediaModalEscapeHandler) return;
      this.mediaModalEscapeHandler = event => this.onMediaModalEscapeKey(event);
      document.addEventListener('keydown', this.mediaModalEscapeHandler, true);
    },
    unbindMediaModalEscapeKey() {
      if (!this.mediaModalEscapeHandler) return;
      document.removeEventListener(
        'keydown',
        this.mediaModalEscapeHandler,
        true
      );
      this.mediaModalEscapeHandler = null;
    },
    // Aceleres -- roda na montagem do painel: se a mídia recém-fechada tinha
    // sido aberta pela lista de mídias, a lista reabre na aba de origem.
    restoreMediaDetailsModal() {
      const target = takeContactMediaReturn(this.currentChat?.id);
      if (!target) return;

      this.setMediaDetailsTab(target.tab || 'media');
    },
    setMediaDetailsTab(tab) {
      // A busca e da aba: levar o texto de Documentos para Links devolveria
      // uma lista vazia sem o utilizador perceber porque.
      this.mediaSearchQuery = '';
      // No computador a seleccao pertence a aba: levar os itens marcados pra
      // outra lista faria a barra agir sobre o que nao esta em tela.
      //
      // No telemovel e o contrario, a pedido: a seleccao sobrevive a troca de
      // aba para se poder juntar uma midia, um documento e um link no mesmo
      // encaminhamento. Quem limpa passa a ser so a seta de voltar da barra.
      if (!this.isMobileViewport) this.clearItemSelection();
      this.activeMediaTab = tab;
      this.showMediaDetailsModal = true;
      // Reutiliza o historico iniciado na abertura do perfil. A marca da
      // conversa evita repetir pedidos ao abrir a lista ou trocar de aba;
      // se a leitura falhou, esta abertura permite tentar novamente.
      this.loadConversationHistoryMedia();
      if (tab === 'links') this.loadLinkPreviews();
    },
    async loadAllConversationMedia() {
      if (
        this.isLoadingAllConversationMedia ||
        this.hasLoadedAllConversationMedia ||
        !this.contact?.id
      ) {
        return;
      }

      this.isLoadingAllConversationMedia = true;
      try {
        const { data } = await ContactAPI.getConversations(this.contact.id);
        const conversations = data?.payload || [];
        const responses = await Promise.all(
          conversations.map(conversation =>
            ConversationAPI.getAllAttachments(conversation.id)
          )
        );

        this.allConversationAttachments = responses.flatMap(
          (response, conversationIndex) =>
            (response.data?.payload || []).map(
              (attachment, attachmentIndex) => ({
                ...attachment,
                id:
                  attachment.id ||
                  `conversation-${conversationIndex}-attachment-${attachmentIndex}`,
                // Aceleres -- guarda de qual conversa o anexo veio; e o que
                // permite "ir para a mensagem" sair da conversa aberta.
                conversation_display_id: conversations[conversationIndex]?.id,
              })
            )
        );
        this.hasLoadedAllConversationMedia = true;
      } catch (error) {
        useAlert('Não foi possível carregar as mídias de todas as conversas.');
      } finally {
        this.isLoadingAllConversationMedia = false;
      }
    },
    isCurrentConversationAttachment(attachment) {
      const url = attachment?.data_url || attachment?.dataUrl || '';
      if (!url) return false;

      return this.galleryAttachments.some(
        item => (item.data_url || item.dataUrl) === url
      );
    },
    openMediaPreviewItem(item) {
      if (this.selectingInsteadOfOpening(item)) return;

      // Aceleres: no painel de Links, o clique na mensagem deve levar ao
      // balao de origem. A URL continua disponivel no cartao, mas abrir uma
      // nova aba aqui fazia o clique parecer sem efeito no contexto da
      // conversa e nunca executava o salto para `messageId`.
      if (item?.type === 'link') {
        this.goToItemMessage(item);
        return;
      }

      // Aceleres -- imagem/video da conversa aberta vao para o GalleryView, o
      // mesmo visualizador dos baloes do chat. Documento, link e midia de outra
      // conversa seguem abrindo em nova guia.
      //
      // Aceleres 2026-08-17: a condicao nao olhava o TIPO, so a conversa. Um
      // documento da conversa aberta entrava aqui e ia parar no GalleryView,
      // que e visualizador de imagem/video — dai o PDF nao abrir no leitor e o
      // resto nao baixar. Documento e link tem cada um o seu caminho, logo
      // abaixo.
      const isMediaItem = item?.type !== 'document' && item?.type !== 'link';
      if (
        isMediaItem &&
        item?.attachment &&
        this.isCurrentConversationAttachment(item.attachment)
      ) {
        this.openInConversationViewer(item.attachment, this.galleryAttachments);
        return;
      }

      if (!item?.url) return;

      if (item.type === 'document') {
        if (this.isPdfItem(item)) {
          // Aceleres 2026-08-17: o PDF abre SEMPRE no leitor desta pagina.
          // Antes, sem a mensagem na store, caia numa aba nova — e isso
          // acontecia justamente nos documentos do historico (outra conversa,
          // ou pagina de mensagens ainda nao carregada), que sao a maioria do
          // que se abre por esta lista. As acoes que precisam da mensagem
          // continuam protegidas dentro do leitor, entao nao ha o que agir
          // sobre dados vazios; o que se perde sem ela e so poder reagir ou
          // fixar, nao o ver o ficheiro.
          this.openPdfViewerItem(item, this.findMessageForItem(item));
          return;
        }

        this.downloadDocumentItem(item);
        return;
      }

      window.open(item.url, '_blank', 'noopener,noreferrer');
    },
    // O visualizador vive na ConversationBox para sobreviver ao fechamento do
    // painel quando uma midia da conversa abre. A foto do perfil mantem o
    // painel aberto para que a animacao possa voltar ao avatar visivel.
    openInConversationViewer(attachment, allAttachments) {
      // Aceleres -- quem abriu a midia DE DENTRO da lista de midias volta pra
      // ela ao fechar, na mesma aba. Quem abriu pela faixa de miniaturas da
      // ficha (ou pela foto do contato) volta pra ficha, como antes.
      setContactMediaReturn(
        this.showMediaDetailsModal
          ? {
              conversationId: this.currentChat?.id,
              tab: this.activeMediaTab,
            }
          : null
      );
      this.closeMediaDetailsModal();
      emitter.emit(BUS_EVENTS.OPEN_CONTACT_MEDIA_VIEWER, {
        attachment,
        allAttachments,
      });
      if (!attachment.aceleresContactAvatar) {
        this.updateUISettings({ is_contact_sidebar_open: false });
      }
    },
    // Aceleres -- o overlay do painel esta no teto do z-index (2147483647) e os
    // visualizadores vivem dentro de .conversation-details-wrap com 2147483500,
    // entao ficariam atras do painel. Rebaixamos o painel enquanto um deles
    // estiver aberto, em vez de fecha-lo, para o usuario voltar na aba de midia.
    syncOverlayPanelStacking(isOpen) {
      document.body?.classList?.toggle('acl-contact-overlay-open', !!isOpen);
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div
    class="contact-details-info relative box-border w-full max-w-full overflow-x-hidden !px-4 !pb-4 !pt-4 md:!px-8 md:!pt-6"
  >
    <nav
      class="contact-details-info__tabs flex w-full min-w-0 overflow-x-auto overscroll-x-contain border-b border-solid border-[rgba(15,23,42,0.08)] bg-white md:grid md:grid-cols-4 md:overflow-visible"
      role="tablist"
      aria-label="Informações do contato"
    >
      <button
        v-for="tab in [
          { id: 'actions', label: 'Ações' },
          { id: 'details', label: 'Detalhes' },
          { id: 'conversation-history', label: 'Histórico de conversas' },
          { id: 'purchase-history', label: 'Histórico de compras' },
        ]"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeContactTab === tab.id"
        class="relative flex shrink-0 items-center justify-center whitespace-nowrap rounded-none border-0 bg-transparent px-3 py-[0.8125rem] text-center text-[0.8125rem] font-semibold leading-tight tracking-[0.01em] transition-colors after:absolute after:inset-x-0 after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:content-[''] hover:text-[#111b21] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00a884] focus-visible:outline-offset-[-2px] md:min-w-0 md:whitespace-normal md:break-words md:px-1.5"
        :class="
          activeContactTab === tab.id
            ? 'text-[#00a884] after:bg-[#00a884]'
            : 'text-[#667781] after:bg-transparent'
        "
        @click="selectContactTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </nav>
    <div class="flex flex-col w-full gap-4 text-left rtl:text-right">
      <div v-show="activeContactTab === 'actions'" class="flex flex-col w-full gap-4">
      <InstagramMergeSuggestions
        :contact-id="contact.id"
        :is-instagram-contact="isInstagramConversation"
        :is-messenger-contact="isMessengerConversation"
        @completed="refreshAfterInstagramMerge"
      />
      <section class="contact-details-info__hero pb-[18px] pt-6">
        <!-- Aceleres -- com foto, o avatar vira botao e abre a foto ampliada
             (mesmo gesto do WhatsApp). Sem foto continua um elemento comum:
             nao ha nada pra ampliar nas iniciais. -->
        <button
          v-if="showAvatar && hasContactPhoto"
          type="button"
          class="contact-details-info__avatar-button"
          :aria-label="`Ver a foto de ${contactDisplayName}`"
          @click="openAvatarViewer"
        >
          <Avatar
            :src="contactAvatarSrc"
            :name="contactDisplayName"
            :status="contact.availability_status"
            :size="isMobileViewport ? 104 : 120"
            hide-offline-status
            rounded-full
            class="contact-details-info__avatar"
          />
        </button>
        <Avatar
          v-else-if="showAvatar"
          :src="contactAvatarSrc"
          :name="contactDisplayName"
          :status="contact.availability_status"
          :size="isMobileViewport ? 104 : 120"
          hide-offline-status
          rounded-full
          class="contact-details-info__avatar"
        />

        <woot-modal
          :show="isWhatsappGroup && isEditingName"
          :on-close="cancelNameEdit"
          :show-close-button="false"
          :close-on-backdrop-click="!isSavingGroupName"
          container-class="!w-[32rem] !max-w-[calc(100vw-2rem)] !overflow-hidden !rounded-2xl"
        >
          <section class="bg-n-solid-1" aria-labelledby="group-name-editor-title">
            <header class="flex items-start gap-3 border-b border-n-weak px-6 py-5">
              <div class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand">
                <span class="i-lucide-pencil size-5" aria-hidden="true" />
              </div>
              <div class="min-w-0 flex-1">
                <h3 id="group-name-editor-title" class="!m-0 text-lg font-semibold text-n-slate-12">Editar grupo</h3>
                <p class="!mb-0 !mt-1 text-sm text-n-slate-11">Atualize o nome e a descrição no WhatsApp.</p>
              </div>
              <button type="button" aria-label="Fechar" :disabled="isSavingGroupName" class="flex size-8 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-slate-3 disabled:opacity-40" @click="cancelNameEdit">
                <span class="i-lucide-x size-5" aria-hidden="true" />
              </button>
            </header>
            <form class="!m-0 flex !w-full !self-stretch flex-col gap-5 !px-6 !py-5" @submit.prevent="saveNameEdit">
              <div class="flex flex-col gap-2">
                <label for="group-name-input" class="text-sm font-medium text-n-slate-12">Nome do grupo</label>
                <div class="relative flex items-center gap-2">
                  <input id="group-name-input" ref="nameInput" v-model="editName" type="text" maxlength="100" :disabled="isSavingGroupName" class="!m-0 !h-11 !min-w-0 !flex-1 !rounded-xl !border-n-weak !bg-n-slate-2 !text-sm" @keydown.esc.prevent="cancelNameEdit" />
                  <button type="button" :aria-label="addGroupEmojiLabel" :disabled="isSavingGroupName" class="flex size-10 shrink-0 items-center justify-center rounded-xl border border-n-weak text-n-slate-11 hover:bg-n-slate-3" @click="toggleGroupEmojiPicker">
                    <span class="i-lucide-smile-plus size-5" aria-hidden="true" />
                  </button>
                  <EmojiInput v-if="showGroupEmojiPicker" v-on-clickaway="hideGroupEmojiPicker" class="contact-details-info__emoji-picker" :on-click="appendGroupEmoji" scroll-sync-tabs />
                </div>
                <span class="text-right text-xs text-n-slate-10">{{ editName.length }}/100</span>
              </div>
              <div class="flex flex-col gap-2">
                <label for="group-description-input" class="text-sm font-medium text-n-slate-12">Descrição</label>
                <textarea id="group-description-input" v-model="editGroupDescription" rows="3" :disabled="isSavingGroupName" placeholder="Descreva o propósito do grupo" class="!m-0 !rounded-xl !border-n-weak !bg-n-slate-2 !text-sm" />
              </div>
              <p v-if="groupNameEditError" role="alert" class="!m-0 rounded-xl bg-n-ruby-2 p-3 text-sm text-n-ruby-11">{{ groupNameEditError }}</p>
              <div class="flex justify-end gap-2 border-t border-n-weak pt-4">
                <button type="button" :disabled="isSavingGroupName" class="h-10 rounded-xl border border-n-weak px-4 text-sm text-n-slate-11 hover:bg-n-slate-3 disabled:opacity-40" @click="cancelNameEdit">Cancelar</button>
                <button type="submit" :disabled="isSavingGroupName" class="flex h-10 items-center gap-2 rounded-xl bg-n-brand px-4 text-sm font-semibold text-white disabled:opacity-40">
                  <span :class="isSavingGroupName ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-check'" class="size-4" aria-hidden="true" />
                  {{ isSavingGroupName ? 'Salvando…' : 'Salvar alterações' }}
                </button>
              </div>
            </form>
          </section>
        </woot-modal>
        <div class="contact-details-info__name-row">
          <h3 class="contact-details-info__name !text-xl md:!text-2xl">
            <span class="hidden md:inline">{{ contactDisplayName }}</span>
            <span class="md:hidden">{{ mobileContactName }}</span>
          </h3>
          <button
            v-if="isWhatsappGroup && canEditGroupName"
            v-tooltip.top="editGroupNameLabel"
            type="button"
            class="contact-details-info__name-edit-trigger"
            :aria-label="editGroupNameLabel"
            @click="startEditingName"
          >
            <span class="i-lucide-pencil" />
          </button>
          <button
            v-else-if="isWhatsappGroup"
            v-tooltip.top="groupPermissionInfoLabel"
            type="button"
            class="contact-details-info__name-edit-trigger"
            :aria-label="groupPermissionInfoLabel"
            @click="openGroupPermissionModal"
          >
            <span class="i-lucide-info" />
          </button>
        </div>

        <div v-if="isTeamNumber" class="contact-details-info__team-number">
          <TeamNumberBadge />
        </div>

        <p v-if="isWhatsappGroup" class="contact-details-info__group-meta">
          <span>{{ groupTypeLabel }}</span>
          <span aria-hidden="true">{{ groupMetaSeparator }}</span>
          <button
            type="button"
            class="contact-details-info__group-members"
            @click="selectGroupMobileSection('members')"
          >
            {{ groupMemberLabel }}
          </button>
        </p>

        <a
          v-if="shouldShowContactDisplayPhone && isInstagramConversation"
          :href="instagramProfileUrl"
          class="contact-details-info__phone contact-details-info__instagram-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="hidden md:inline">{{ contactDisplayPhone }}</span>
          <span class="md:hidden">{{ mobileContactPhone }}</span>
        </a>
        <p
          v-else-if="shouldShowContactDisplayPhone"
          class="contact-details-info__phone"
        >
          <span class="hidden md:inline">{{ contactDisplayPhone }}</span>
          <span class="md:hidden">{{ mobileContactPhone }}</span>
        </p>

        <div
          v-if="!isMergeBlockedForGroup || hasInstagramProfile"
          class="contact-details-info__actions !mx-auto !grid !w-full !gap-0 max-md:[&_.contact-details-info__action-icon]:!size-8 md:!mx-0 md:!flex md:!w-auto md:!max-w-none md:!gap-[14px]"
          :class="
            isMergeBlockedForGroup
              ? '!max-w-[17rem] !grid-cols-1'
              : hasInstagramProfile
                ? '!max-w-[17rem] !grid-cols-3 md:!max-w-[22rem] md:!grid-cols-4'
                : '!max-w-[17rem] !grid-cols-3'
          "
        >
          <button
            v-if="!isMergeBlockedForGroup"
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-h-[92px] md:!gap-[10px] md:!px-2 md:!text-[0.9375rem]"
            @click="openAceleresNewMessage"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span
                class="i-ph-chat-circle-dots contact-details-info__action-icon"
              />
            </span>
            <span>{{ $t('CONTACT_PANEL.NEW_MESSAGE') }}</span>
          </button>
          <button
            v-if="!isMergeBlockedForGroup"
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-h-[92px] md:!gap-[10px] md:!px-2 md:!text-[0.9375rem]"
            @click="openEmail"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span class="i-lucide-mail contact-details-info__action-icon" />
            </span>
            <span>{{ emailActionTitle }}</span>
          </button>
          <a
            v-if="hasInstagramProfile"
            :href="instagramProfileUrl"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-h-[92px] md:!gap-[10px] md:!px-2 md:!text-[0.9375rem]"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span
                class="i-ri-instagram-fill contact-details-info__action-icon"
              />
            </span>
            <span>{{ $t('CONTACT_PANEL.VIEW_PROFILE') }}</span>
          </a>
          <button
            type="button"
            class="contact-details-info__action !min-w-0 !min-h-[4.5rem] !gap-2 !px-1 !text-xs md:!min-h-[92px] md:!gap-[10px] md:!px-2 md:!text-[0.9375rem]"
            v-if="!isMergeBlockedForGroup"
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          >
            <span
              class="contact-details-info__action-icon-wrap !size-11 md:!w-[70px] md:!h-[52px]"
            >
              <span
                class="i-ph-arrows-merge contact-details-info__action-icon"
              />
            </span>
            <span>{{ $t('CONTACT_PANEL.MERGE_CONTACT') }}</span>
          </button>
        </div>
      </section>

      <nav
        v-if="isWhatsappGroup"
        class="-mx-4 grid grid-cols-3 gap-1 border-y border-n-weak bg-n-solid-1 px-2 py-2 md:hidden"
        :aria-label="groupMobileNavigationLabel"
      >
        <button
          v-for="section in groupMobileSections"
          :key="section.id"
          type="button"
          class="flex min-h-11 min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-xs font-semibold transition-colors"
          :class="
            activeGroupMobileSection === section.id
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-current="
            activeGroupMobileSection === section.id ? 'location' : undefined
          "
          @click="selectGroupMobileSection(section.id)"
        >
          <span :class="section.icon" class="size-4" aria-hidden="true" />
          <span class="truncate">{{ section.label }}</span>
        </button>
      </nav>

      <div
        v-if="isWhatsappGroup"
        id="contact-group-members-mobile-target"
        class="-mx-4 md:hidden"
      />

      <!-- Aceleres 2026-08-19: midias, documentos e links tambem no grupo.
           A seccao estava escondida por `v-if="!isWhatsappGroup"`, mas nada do
           que ela mostra e do contacto: a contagem, as quatro pre-visualizacoes
           e a lista saem TODAS da conversa aberta (`currentChat`) — anexos da
           store mais `getAllAttachments`/`getAllLinks` por `conversation_id`.
           Numa conversa de grupo esses pedidos sao os mesmos e devolvem o
           mesmo formato, entao o gate so apagava da vista o que ja existia.
           Fica antes de GroupMembers (que o ContactPanel desenha a seguir),
           como no WhatsApp: a midia acima, os participantes por baixo. -->
      <section
        class="contact-details-media max-md:scroll-mt-16"
        :data-group-section="isWhatsappGroup ? 'media' : undefined"
        :aria-label="mediaLinksDocsTitle"
      >
        <button
          type="button"
          class="contact-details-media__header"
          @click="openMediaDetailsModal"
        >
          <span class="contact-details-media__title-wrap">
            <span class="i-lucide-images contact-details-media__title-icon" />
            <span class="contact-details-media__title">
              {{ mediaLinksDocsTitle }}
            </span>
          </span>
          <span class="contact-details-media__count">
            {{ mediaLinksDocsCount ?? '…' }}
          </span>
        </button>
        <!-- Aceleres 2026-08-20: no telemóvel, a faixa permite percorrer até
             doze mídias. No computador, as classes `md:` preservam a grade de
             quatro itens que já existia. -->
        <div
          class="contact-details-media__preview-list !flex !snap-x !snap-mandatory !gap-2 !overflow-x-auto md:!grid md:!grid-cols-4 md:!gap-[10px] md:!overflow-visible"
        >
          <button
            v-for="item in mediaPreviewItems"
            :key="item.id"
            type="button"
            class="contact-details-media__preview !aspect-square !w-[76px] !flex-none !snap-start md:!aspect-[1.12/1] md:!w-auto"
            :class="`contact-details-media__preview--${item.type}`"
            :title="item.title"
            @click="openMediaPreviewItem(item)"
          >
            <img
              v-if="guardedMediaSrc(item.thumbUrl)"
              :src="guardedMediaSrc(item.thumbUrl)"
              :alt="item.title"
              class="contact-details-media__preview-image"
              :class="{
                'contact-details-media__preview-image--sticker':
                  item.kind === 'sticker',
              }"
            />
            <video
              v-else-if="
                item.kind === 'video' &&
                guardedMediaSrc(guardedPreviewUrl(item))
              "
              :src="guardedMediaSrc(guardedPreviewUrl(item))"
              class="contact-details-media__preview-image contact-details-media__preview-video"
              muted
              playsinline
              preload="metadata"
              @loadedmetadata="seekVideoPreviewFrame"
            />
            <span v-else class="contact-details-media__preview-placeholder">
              <span
                class="contact-details-media__preview-icon"
                :class="
                  item.type === 'link' ? 'i-lucide-link' : 'i-lucide-video'
                "
              />
            </span>
            <span v-if="item.isGif" class="contact-details-media__preview-tag">
              GIF
            </span>
            <span
              v-else-if="item.kind === 'video' && item.url"
              class="contact-details-media__preview-play"
            >
              <span class="i-lucide-play" />
            </span>
          </button>
          <button
            v-if="isMobileViewport && mediaPreviewItems.length"
            type="button"
            class="contact-details-media__preview !flex !aspect-square !w-[76px] !flex-none !snap-start !items-center !justify-center !bg-white md:!hidden"
            :aria-label="viewAllMediaLabel"
            :title="viewAllMediaLabel"
            @click="openMediaDetailsModal"
          >
            <span class="i-lucide-arrow-right !size-7 !text-black" />
          </button>
        </div>
      </section>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div
          v-if="isWhatsappGroup"
          class="flex flex-col w-full gap-2 text-sm text-n-slate-11"
        >
          <span v-if="whatsappGroup.description" class="break-words">
            {{ whatsappGroup.description }}
          </span>
        </div>
      </div>
      <!-- Aceleres 2026-08-17: o editar contato recebe o MESMO alvo de Teleport
           que a lista de mídias já usa, para nascer na mesma caixa e com a
           mesma medida. Antes ele era uma gaveta presa à janela, com 990px a
           partir da borda direita, enquanto a lista cobre exatamente o painel
           — dois tamanhos diferentes para duas telas que abrem do mesmo sítio. -->
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        :can-delete="isAdmin && !isTrainingContactContext"
        :teleport-target="mediaModalTarget"
        @cancel="closeEditModal"
        @delete="toggleDeleteModal"
      />
      <ContactMergeModal ref="mergeModal" :primary-contact="contact" />
    </div>
      </div>

      <div v-if="activeContactTab === 'details'" class="contact-details-info__tab-panel">
        <ContactIdentities
          ref="contactIdentities"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          hide-when-single
          class="mb-4"
        />
        <AceleresClientDetails :contact="contact" />
      </div>

      <div v-else-if="activeContactTab === 'conversation-history'" class="contact-details-info__tab-panel">
        <h3 class="contact-details-info__placeholder-title">Histórico de conversas</h3>
        <p v-if="isLoadingConversationHistory" class="contact-details-info__placeholder">Carregando conversas...</p>
        <p v-else-if="!conversationHistory.length" class="contact-details-info__placeholder">Nenhuma conversa encontrada.</p>
        <div v-else class="contact-details-info__conversation-list">
          <button
            v-for="conversation in conversationHistory"
            :key="conversation.id"
            type="button"
            class="contact-details-info__conversation-item"
            @click="openConversation(conversation)"
          >
            <span class="contact-details-info__conversation-heading">
              <span class="contact-details-info__conversation-title">
                {{ conversation.title || `Conversa #${conversation.id}` }}
              </span>
              <span
                class="contact-details-info__conversation-status"
                :class="`contact-details-info__conversation-status--${conversationStatusKey(conversation) || 'unknown'}`"
              >
                {{ conversationStatusLabel(conversation) }}
              </span>
            </span>
            <span
              v-if="conversationAssigneeName(conversation)"
              class="contact-details-info__conversation-meta"
            >
              Vendedor responsável: {{ conversationAssigneeName(conversation) }}
            </span>
            <span
              v-if="conversationResolvedAt(conversation)"
              class="contact-details-info__conversation-meta"
            >
              Concluído em {{ conversationResolvedAt(conversation) }}
            </span>
            <span
              v-if="conversationInstanceName(conversation)"
              class="contact-details-info__conversation-meta"
            >
              Instância: {{ conversationInstanceName(conversation) }}
            </span>
          </button>
        </div>
      </div>

      <div v-else-if="activeContactTab === 'purchase-history'" class="contact-details-info__tab-panel">
        <h3 class="contact-details-info__placeholder-title">Histórico de compras</h3>
        <p class="contact-details-info__placeholder">Em breve: histórico de compras.</p>
      </div>
    <Teleport v-if="pdfViewerItem" to=".conversation-details-wrap">
      <ContactPdfViewer
        :message="pdfViewerMessage || {}"
        :url="pdfViewerItem.url"
        :file-name="pdfViewerItem.title"
        @close="closePdfViewer"
      />
    </Teleport>
    <Teleport to="body">
      <div v-if="showDeleteModal" class="contact-info-delete-modal-layer">
        <woot-delete-modal
          v-model:show="showDeleteModal"
          :on-close="closeDelete"
          :on-confirm="confirmDeletion"
          :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
          :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
          :message-value="confirmDeleteMessage"
          :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
          :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
          :disable-confirm="!contactRemoval.canConfirm.value"
        >
          <div class="flex flex-col gap-3 text-sm">
            <p
              v-if="contactRemoval.isLoadingPreview.value"
              class="mb-0 text-n-slate-11"
            >
              {{ $t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.LOADING_PREVIEW') }}
            </p>
            <p
              v-else-if="contactRemoval.previewFailed.value"
              class="mb-0 text-n-ruby-11"
            >
              {{ $t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.PREVIEW_FAILED') }}
            </p>
            <template v-else>
              <p
                v-if="contactRemoval.resolvedConversations.value > 0"
                class="mb-0 text-n-slate-11"
              >
                {{
                  $t(
                    'CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.RESOLVED_PRESERVED',
                    { count: contactRemoval.resolvedConversations.value }
                  )
                }}
              </p>
              <p
                v-if="contactRemoval.openConversations.value > 0"
                class="mb-0 text-n-slate-11"
              >
                {{
                  $t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.OPEN_PRESERVED', {
                    count: contactRemoval.openConversations.value,
                  })
                }}
              </p>
              <p
                v-if="contactRemoval.purgedConversations.value > 0"
                class="mb-0 font-medium text-n-ruby-11"
              >
                {{
                  $t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.PURGED_DESTROYED', {
                    count: contactRemoval.purgedConversations.value,
                  })
                }}
              </p>
              <div
                v-if="contactRemoval.requiresCode.value"
                class="flex flex-col gap-1.5"
              >
                <label
                  for="contact-info-removal-code"
                  class="text-sm text-n-slate-12"
                >
                  {{
                    $t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CODE_LABEL', {
                      code: contactRemoval.expectedCode.value,
                    })
                  }}
                </label>
                <input
                  id="contact-info-removal-code"
                  v-model="contactRemoval.typedCode.value"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  maxlength="4"
                  class="w-24 px-3 py-2 text-base tracking-[0.4em] text-center border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
                />
              </div>
            </template>
          </div>
        </woot-delete-modal>
      </div>
    </Teleport>
    <Teleport :to="mediaModalTarget || 'body'">
      <section
        v-if="showMediaDetailsModal"
        class="contact-details-media-modal"
        :class="{
          'contact-details-media-modal--detached': !mediaModalTarget,
        }"
        aria-label="Mídias, documentos e links"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <!-- Aceleres -- com itens selecionados o cabecalho vira barra de
             acoes, no lugar do botao de voltar. Encaminhar vale nas tres abas;
             baixar e destacar aparecem onde fazem sentido (a midia tem as
             tres). As acoes sao as mesmas funcoes em qualquer aba: elas agem
             sobre `selectedItems`, que ja e o da aba ativa. -->
        <header
          class="contact-details-media-modal__header"
          :class="{
            'contact-details-media-modal__header--media':
              activeMediaTab === 'media',
          }"
        >
          <template v-if="hasItemSelection">
            <button
              v-tooltip.top="'Cancelar seleção'"
              type="button"
              class="contact-details-media-modal__back contact-details-media-modal__back--cancel"
              aria-label="Cancelar seleção"
              @click.stop="clearItemSelection"
            >
              <!-- Aceleres 2026-08-19: no telemovel sai a X e entra a seta —
                   e o mesmo gesto de "voltar" do resto do ecra. Mesma accao,
                   dois icones; quem escolhe e a media query. -->
              <span
                class="i-lucide-arrow-left contact-details-media-modal__icon-mobile"
              />
              <span
                class="i-lucide-x contact-details-media-modal__icon-desktop"
              />
            </button>
            <!-- Aceleres 2026-08-18: a palavra vive num span proprio para o
                 telemovel a poder esconder e ficar so o numero, como pedido.
                 Um span a menos e melhor do que duplicar o cabecalho todo com
                 um `v-if` de largura. -->
            <span class="contact-details-docs__selection-count">
              {{ selectedItemIds.length
              }}<span class="contact-details-docs__selection-word">
                selecionado{{ selectedItemIds.length === 1 ? '' : 's' }}</span
              >
            </span>
            <span class="contact-details-docs__selection-actions">
              <button
                v-if="
                  activeMediaTab === 'documents' || activeMediaTab === 'media'
                "
                v-tooltip.top="'Baixar'"
                type="button"
                class="contact-details-media-modal__back contact-details-media-modal__back--download"
                aria-label="Baixar selecionados"
                @click.stop="downloadSelectedDocuments"
              >
                <span class="i-lucide-download" />
              </button>
              <button
                v-if="activeMediaTab === 'links' || activeMediaTab === 'media'"
                v-tooltip.top="'Destacar'"
                type="button"
                class="contact-details-media-modal__back contact-details-media-modal__back--star"
                aria-label="Destacar selecionados"
                @click.stop="starSelectedItems"
              >
                <span class="i-lucide-star" />
              </button>
              <!-- Aceleres 2026-08-18: fixar. Aparece SO no telemovel (a regra
                   `--pin` esta escondida por omissao e so a media query de
                   telemovel a mostra) e SO com um item selecionado -- fixar
                   age sobre uma mensagem de cada vez. -->
              <button
                v-if="canPinSelection"
                type="button"
                class="contact-details-media-modal__back contact-details-media-modal__back--pin"
                aria-label="Fixar mensagem selecionada"
                @click.stop="pinSelectedItem"
              >
                <span class="i-lucide-pin" />
              </button>
              <button
                v-tooltip.top="'Encaminhar'"
                type="button"
                class="contact-details-media-modal__back"
                aria-label="Encaminhar selecionados"
                @click.stop="openForwardDocumentsModal"
              >
                <svg
                  class="contact-details-media-modal__forward-mobile-icon block md:hidden"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="12" fill="rgba(0, 0, 0, 0.38)" />
                  <path
                    fill="#fff"
                    transform="translate(4 4) scale(0.67)"
                    d="M14 8V4l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z"
                  />
                </svg>
                <Icon
                  icon="i-tabler-corner-up-right-double"
                  class="hidden size-5 md:inline-flex"
                  aria-hidden="true"
                />
              </button>
            </span>
          </template>
          <button
            v-else
            v-tooltip.top="'Voltar para os dados do contato'"
            type="button"
            class="contact-details-media-modal__back"
            aria-label="Voltar para os dados do contato"
            @click.stop="closeMediaDetailsModal"
          >
            <span class="i-lucide-arrow-left" />
          </button>
          <span
            v-if="!hasItemSelection && activeMediaTab === 'media'"
            class="ml-1 text-base font-semibold text-n-slate-12 md:hidden"
          >
            {{ allMediaTitle }}
          </span>
          <!-- Aceleres 2026-08-20: no telemovel, a busca fica na barra de
               cima, ao lado da seta. Continua a ser um unico campo para cada
               aba pesquisavel; no computador a regra base mantem-no oculto. -->
          <label
            v-if="!hasItemSelection && isSearchableMediaTab"
            class="contact-details-media-search"
          >
            <span
              class="contact-details-media-search__icon i-lucide-search"
              aria-hidden="true"
            />
            <input
              v-model="mediaSearchQuery"
              type="text"
              class="contact-details-media-search__input reset-base"
              :placeholder="mediaSearchPlaceholder"
              :aria-label="mediaSearchPlaceholder"
            />
            <button
              v-if="mediaSearchQuery"
              type="button"
              class="contact-details-media-search__clear i-lucide-x"
              :aria-label="clearMediaSearchLabel"
              @click="mediaSearchQuery = ''"
            />
          </label>
        </header>
        <div class="contact-details-media-modal__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'media'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'media' }"
            @click.stop.prevent="setMediaDetailsTab('media')"
          >
            Mídia
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'documents'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'documents' }"
            @click.stop.prevent="setMediaDetailsTab('documents')"
          >
            Documentos
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeMediaTab === 'links'"
            class="contact-details-media-modal__tab"
            :class="{ 'contact-details-media-modal__tab--active': activeMediaTab === 'links' }"
            @click.stop.prevent="setMediaDetailsTab('links')"
          >
            Links
          </button>
        </div>
        <div
          class="contact-details-media-modal__body"
          :class="{
            'contact-details-media-modal__body--media':
              activeMediaTab === 'media',
          }"
        >
          <!-- Aceleres -- o rotulo "Neste mes" so fica na aba de Midia; em
               Documentos e Links ele foi retirado a pedido. -->
          <p
            v-if="activeMediaTab === 'media'"
            class="contact-details-media-modal__section-title"
          >
            Neste mês
          </p>
          <div
            v-if="activeMediaTab === 'media' && activeMediaTabItems.length"
            class="contact-details-media-modal__media-grid"
          >
            <!-- Aceleres 2026-08-17 -- deixou de ser <button> e virou div com
                 role="button": a caixa de selecao e ela propria um <button>, e
                 botao dentro de botao e HTML invalido (o navegador quebra a
                 arvore e o clique de dentro deixa de chegar). O teclado
                 continua abrindo a midia, agora por keydown explicito. -->
            <div
              v-for="item in activeMediaTabItems"
              :key="item.id"
              class="contact-details-media-modal__media-card"
              :class="{
                'contact-details-media-modal__media-card--selected':
                  isItemSelected(item),
              }"
              role="button"
              tabindex="0"
              :title="item.title"
              @click="onMediaCardClick(item)"
              @keydown.enter.space.self.prevent="openMediaPreviewItem(item)"
              @touchstart.passive="onMediaTouchStart(item, $event)"
              @touchmove.passive="onMediaTouchMove($event)"
              @touchend="onMediaTouchEnd"
              @touchcancel="onMediaTouchEnd"
              @contextmenu="onMediaContextMenu($event)"
            >
              <img
                v-if="guardedMediaSrc(item.thumbUrl)"
                :src="guardedMediaSrc(item.thumbUrl)"
                :alt="item.title"
                class="contact-details-media-modal__media-image"
                :class="{
                  'contact-details-media-modal__media-image--sticker':
                    item.kind === 'sticker',
                }"
              />
              <video
                v-else-if="
                  item.kind === 'video' &&
                  guardedMediaSrc(guardedPreviewUrl(item))
                "
                :src="guardedMediaSrc(guardedPreviewUrl(item))"
                class="contact-details-media-modal__media-image contact-details-media-modal__media-video"
                muted
                playsinline
                preload="metadata"
                @loadedmetadata="seekVideoPreviewFrame"
              />
              <span v-else class="i-lucide-image-off contact-details-media-modal__fallback-icon" />
              <span
                v-if="item.isGif || item.kind === 'video'"
                class="contact-details-media-modal__media-tag"
                :class="{
                  'contact-details-media-modal__media-tag--video':
                    !item.isGif && item.kind === 'video',
                }"
              >
                <span v-if="item.isGif">GIF</span>
                <span v-else class="i-lucide-video" />
              </span>
              <!-- Aparece so no hover (ou quando ja selecionada). Reaproveita
                   toggleItemSelection/isItemSelected da aba de documentos: a
                   barra de acoes do cabecalho ja e generica e passa a valer
                   para a midia sem nenhuma logica nova. -->
              <button
                type="button"
                class="contact-details-docs__checkbox contact-details-docs__checkbox--hover contact-details-media-modal__media-check"
                :class="{
                  'contact-details-docs__checkbox--on': isItemSelected(item),
                }"
                :aria-pressed="isItemSelected(item)"
                :aria-label="`Selecionar ${item.title}`"
                @click.stop="toggleItemSelection(item)"
              />
            </div>
          </div>
          <!-- Aceleres -- documentos como baloes de conversa, igual WhatsApp:
               recebido a esquerda em branco, enviado a direita em verde, com
               tipo, tamanho, horario e (so no enviado) o estado de entrega. -->
          <div
            v-else-if="
              activeMediaTab === 'documents' && visibleMediaTabItems.length
            "
            class="contact-details-docs contact-details-docs--documents"
          >
            <div
              v-for="item in visibleMediaTabItems"
              :key="item.id"
              class="contact-details-docs__row group !-mx-2.5 !px-2.5 !py-4 cursor-pointer hover:bg-[#f0efed]"
              :class="{
                'contact-details-docs__row--selected': isItemSelected(item),
              }"
              :title="goToMessageTitle"
              @click="goToItemMessage(item, { keepModalOpen: true })"
              @touchstart.passive="onMediaTouchStart(item, $event)"
              @touchmove.passive="onMediaTouchMove($event)"
              @touchend="onMediaTouchEnd"
              @touchcancel="onMediaTouchEnd"
            >
              <button
                type="button"
                class="contact-details-docs__checkbox contact-details-docs__checkbox--hover"
                :class="{
                  'contact-details-docs__checkbox--on': isItemSelected(item),
                }"
                :aria-pressed="isItemSelected(item)"
                :aria-label="`Selecionar ${item.title}`"
                @click.stop="toggleItemSelection(item)"
              />
              <!-- Aceleres 2026-08-17: o clique de "ir para a mensagem" subiu
                   para a LINHA (igual ao link), entao vale tambem no fundo em
                   volta do balao, e nao so dentro dele. O balao continua a ser
                   um <button> pelo teclado: o Enter dispara um clique que sobe
                   ate a linha. O bloco do arquivo (selo + nome) para o clique
                   e continua a abrir o documento. -->
              <button
                type="button"
                class="contact-details-docs__bubble md:!mr-12"
                :class="{
                  'contact-details-docs__bubble--outgoing': item.isOutgoing,
                  'contact-details-docs__bubble--plain': !item.isOutgoing,
                }"
              >
                <span
                  class="contact-details-docs__doc"
                  role="button"
                  tabindex="0"
                  :title="item.title"
                  @click.stop="openMediaPreviewItem(item)"
                  @keydown.enter.space.prevent.stop="openMediaPreviewItem(item)"
                >
                  <!-- Aceleres 2026-08-19: no telemovel o documento nao
                       mostra a primeira pagina. E o desenho do WhatsApp — selo
                       a esquerda, nome, informacoes por baixo — e poupa
                       descodificar uma imagem por linha numa lista que rola.
                       O `v-if` evita a imagem em vez de a esconder por CSS:
                       escondida, ela seria descarregada na mesma. -->
                  <img
                    v-if="!isMobileViewport && documentPreviewSrc(item)"
                    :src="documentPreviewSrc(item)"
                    :alt="item.title"
                    class="contact-details-docs__preview"
                  />
                  <span class="contact-details-docs__file">
                    <FileIcon
                      v-if="isMobileViewport"
                      class="size-10 shrink-0"
                      :file-type="documentTypeLabel(item).toLowerCase()"
                    />
                    <span
                      v-else
                      class="contact-details-docs__badge"
                      :class="`contact-details-docs__badge--${documentTypeLabel(
                        item
                      ).toLowerCase()}`"
                    >
                      {{ documentTypeLabel(item) }}
                    </span>
                    <span class="contact-details-docs__info">
                      <span class="contact-details-docs__name">
                        {{ item.title }}
                      </span>
                      <span class="contact-details-docs__meta">
                        {{ documentMetaLabel(item) }}
                      </span>
                    </span>
                    <span
                      class="contact-details-docs__download"
                      role="button"
                      tabindex="0"
                      title="Baixar"
                      aria-label="Baixar documento"
                      @click.stop="downloadDocumentItem(item)"
                      @keydown.enter.space.prevent.stop="
                        downloadDocumentItem(item)
                      "
                    >
                      <span class="i-lucide-download" />
                    </span>
                  </span>
                </span>
                <span class="contact-details-docs__footer">
                  <!-- As duas leituras do mesmo carimbo vivem lado a lado e
                       quem escolhe e a media query: no telemovel fica o DIA
                       ("Ontem", "17/08/26") alinhado a esquerda; no computador
                       continua a hora. Dois spans custam menos do que duplicar
                       o rodape inteiro num `v-if` de largura. -->
                  <span class="contact-details-docs__day">
                    {{ documentDayLabel(item) }}
                  </span>
                  <span class="contact-details-docs__time">
                    {{ documentTimeLabel(item) }}
                  </span>
                  <span
                    v-if="documentStatusIcon(item)"
                    class="contact-details-docs__status"
                    :class="[
                      documentStatusIcon(item),
                      {
                        'contact-details-docs__status--read':
                          item.status === 'read',
                      },
                    ]"
                  />
                </span>
              </button>
            </div>
          </div>
          <!-- Aceleres 2026-08-20 -- no telemovel, a aba de Links e uma lista
               de cartoes simples: um retangulo cinzento de cantos redondos com
               o icone generico de ligacao, o endereco e, por baixo, de que
               pagina e. Depois do endereco vem o botao que leva a mensagem no
               chat.

               Saiu o desenho anterior, que imitava a bolha da conversa (avatar,
               "Voce > Fulano", hora, selo de entrega, titulo e descricao da
               previa). O cartao aqui e uma LISTA de enderecos, nao uma copia da
               conversa; a mensagem de origem continua a um toque de distancia,
               agora por um botao explicito em vez de um clique no fundo do
               balao, que ninguem adivinhava.

               Tocar no cartao abre o endereco (`openMediaPreviewItem`, que para
               o tipo `link` abre em nova guia). O botao chama
               `goToItemMessage` SEM `keepModalOpen`, ou seja fecha esta janela
               antes de saltar — senao o modal ficaria por cima da mensagem
               para onde se acabou de ir. -->
          <div
            v-else-if="
              activeMediaTab === 'links' &&
              isMobileViewport &&
              visibleMediaTabItems.length
            "
            class="contact-details-links pt-2.5"
          >
            <div
              v-for="item in visibleMediaTabItems"
              :key="item.id"
              class="contact-details-link-card"
              :class="{
                'contact-details-link-card--selected': isItemSelected(item),
              }"
              @touchstart.passive="onMediaTouchStart(item, $event)"
              @touchmove.passive="onMediaTouchMove($event)"
              @touchend="onMediaTouchEnd"
              @touchcancel="onMediaTouchEnd"
              @contextmenu="onMediaContextMenu($event)"
            >
              <div
                class="contact-details-link-card__main"
                role="button"
                tabindex="0"
                :title="item.url"
                :aria-pressed="isItemSelected(item)"
                @click="onMediaCardClick(item)"
                @keydown.enter.space.prevent="openMediaPreviewItem(item)"
              >
                <span
                  class="contact-details-link-card__icon i-lucide-link-2"
                  aria-hidden="true"
                />
                <span class="contact-details-link-card__text">
                  <span class="contact-details-link-card__url">
                    {{ item.url }}
                  </span>
                  <span class="contact-details-link-card__host">
                    {{ item.host }}
                  </span>
                </span>
              </div>
              <button
                v-if="item.messageId"
                type="button"
                class="contact-details-link-card__go"
                @click.stop="goToItemMessage(item)"
              >
                Ver mensagem
              </button>
            </div>
          </div>
          <!-- Aceleres 2026-08-20 -- no computador permanece o desenho
               original: remetente e destinatario no cabecalho, previa dentro
               do balao e hora no rodape. As classes ja usadas pelos documentos
               concentram toda a aparencia, sem uma segunda copia de CSS. -->
          <div
            v-else-if="
              activeMediaTab === 'links' && visibleMediaTabItems.length
            "
            class="contact-details-docs"
          >
            <div
              v-for="item in visibleMediaTabItems"
              :key="item.id"
              class="contact-details-docs__row group !-mx-2.5 !flex-col !items-stretch !gap-3 !py-5 !px-2.5 cursor-pointer hover:bg-[#f0efed]"
              :class="{
                'contact-details-docs__row--selected': isItemSelected(item),
              }"
              :title="goToMessageTitle"
              @click="goToItemMessage(item, { keepModalOpen: true })"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-3">
                  <Avatar
                    :name="item.senderName"
                    :src="item.senderAvatar"
                    :size="32"
                    rounded-full
                  />
                  <span
                    class="truncate text-[0.9375rem] font-medium text-n-slate-12"
                  >
                    {{ item.displaySender }}
                    <span
                      class="i-lucide-chevron-right mx-1 inline-block size-3 text-n-slate-9"
                    />
                    {{ item.displayRecipient }}
                  </span>
                </div>
                <div class="flex shrink-0 items-center gap-2 text-n-slate-10">
                  <span class="text-sm">
                    {{ documentTimeLabel(item) }}
                  </span>
                  <span class="i-lucide-chevron-right size-5" />
                </div>
              </div>
              <div class="flex min-w-0 items-center gap-[44px] pl-1">
                <button
                  type="button"
                  class="contact-details-docs__checkbox contact-details-docs__checkbox--hover"
                  :class="{
                    'contact-details-docs__checkbox--on': isItemSelected(item),
                  }"
                  :aria-pressed="isItemSelected(item)"
                  :aria-label="`Selecionar ${item.url}`"
                  @click.stop="toggleItemSelection(item)"
                />
                <button
                  type="button"
                  class="contact-details-docs__bubble !mr-12 min-w-0"
                  :class="{
                    'contact-details-docs__bubble--outgoing': item.isOutgoing,
                  }"
                  :title="goToMessageTitle"
                >
                  <span
                    class="contact-details-docs__doc min-w-0 [overflow-wrap:anywhere]"
                    role="button"
                    tabindex="0"
                    :title="item.url"
                    @click.stop="openMediaPreviewItem(item)"
                    @keydown.enter.space.prevent.stop="
                      openMediaPreviewItem(item)
                    "
                  >
                    <span
                      class="flex min-w-0 flex-col gap-1.5 rounded-lg p-2"
                      :class="item.isOutgoing ? 'bg-[#d5f3cf]' : 'bg-[#f5f6f6]'"
                    >
                      <span
                        class="line-clamp-2 text-[0.9375rem] font-semibold leading-snug text-n-slate-12"
                      >
                        {{ item.title }}
                      </span>
                      <span
                        v-if="item.description"
                        class="line-clamp-3 text-[0.9375rem] leading-relaxed text-n-slate-11"
                      >
                        {{ item.description }}
                      </span>
                      <span
                        class="truncate text-[0.9375rem] leading-relaxed text-n-slate-11"
                      >
                        {{ item.host }}
                      </span>
                    </span>
                    <span
                      v-if="item.caption"
                      class="text-[0.9375rem] font-semibold text-n-slate-12"
                    >
                      {{ item.caption }}
                    </span>
                    <span
                      class="text-[15.8px] leading-relaxed text-[#1b8755] underline"
                    >
                      {{ item.url }}
                    </span>
                  </span>
                  <span class="contact-details-docs__footer">
                    <span class="contact-details-docs__time">
                      {{ documentTimeLabel(item) }}
                    </span>
                    <span
                      v-if="documentStatusIcon(item)"
                      class="contact-details-docs__status"
                      :class="[
                        documentStatusIcon(item),
                        {
                          'contact-details-docs__status--read':
                            item.status === 'read',
                        },
                      ]"
                    />
                  </span>
                </button>
              </div>
            </div>
          </div>
          <p v-else class="contact-details-media-modal__empty">
            {{ mediaDetailsEmptyMessage }}
          </p>
        </div>
        <footer class="contact-details-media-modal__footer">
          <button
            type="button"
            class="contact-details-media-modal__all-media"
            :disabled="isLoadingAllConversationMedia || hasLoadedAllConversationMedia"
            @click="loadAllConversationMedia"
          >
            <span class="i-lucide-images" />
            <span>
              {{
                hasLoadedAllConversationMedia
                  ? 'Mídias de todas as conversas carregadas'
                  : isLoadingAllConversationMedia
                    ? 'Carregando mídias...'
                    : 'Ver mídias de todas as conversas'
              }}
            </span>
          </button>
        </footer>
      </section>
    </Teleport>
    <ForwardMessageModal
      v-if="showForwardDocumentsModal && selectedItemsConversationId"
      v-model:show="showForwardDocumentsModal"
      :conversation-id="selectedItemsConversationId"
      :selected-messages="selectedItemMessages"
      :contact-panel-mobile="isMobileViewport"
      @cancel="showForwardDocumentsModal = false"
      @success="onDocumentsForwarded"
    />
    <woot-modal
      v-model:show="showGroupPermissionModal"
      :on-close="closeGroupPermissionModal"
      :show-close-button="false"
      class="group-permission-modal"
    >
      <div class="group-permission-modal__content">
        <p class="group-permission-modal__message">
          {{ groupPermissionMessage }}
        </p>
        <button
          type="button"
          class="group-permission-modal__confirm"
          @click="closeGroupPermissionModal"
        >
          {{ groupPermissionButtonLabel }}
        </button>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped src="./ContactInfo.css" />
